/**
 * Pre-seeded "Official Products" — the six commercial GI products
 * that ship in `products/` (gate-to-shelf, amar-reporter,
 * research-shield, citizen-mirror, cyber-shield, legal-evidence).
 *
 * They appear in the Marketplace grid and Vault sidebar from day one
 * so freshly-deployed engines don't show empty states. The metadata
 * is bilingual (EN + HE) and resolved at render time from the active
 * locale; the underlying `MarketplaceListing` shape is unchanged.
 *
 * Identification:
 *   - Each product has a fixed `OFFICIAL_PRODUCT_IDS` UUID (deterministic
 *     so cards link to the same detail page across reloads / browsers).
 *   - The card renderer uses `isOfficialProduct(id)` to add the
 *     "Built by Growing Intelligence" pill.
 *
 * The shipping `manifest.json` in `products/<slug>/` remains the
 * canonical source of truth; this file is the UI's display copy.
 */

import type {
  BehavioralContract,
  DomainType,
  EntityId,
  Lineage,
  MarketplaceListing,
  PortDefinition,
  PublicKey,
  TrustedTimestamp,
} from '@/api/types';

import type { Locale } from '@/i18n/config';

/** Stable, deterministic root_ids for the official products. The
 *  `00000000-0000-7nnn-` prefix marks them as engineered (not
 *  randomly generated). */
export const OFFICIAL_PRODUCT_IDS = {
  gateToShelf: '00000000-0000-7001-9100-000000000001',
  amarReporter: '00000000-0000-7002-9100-000000000002',
  researchShield: '00000000-0000-7003-9100-000000000003',
  citizenMirror: '00000000-0000-7004-9100-000000000004',
  cyberShield: '00000000-0000-7005-9100-000000000005',
  legalEvidence: '00000000-0000-7006-9100-000000000006',
  constructionPermit: '00000000-0000-7007-9100-000000000007',
  constructionInspection: '00000000-0000-7008-9100-000000000008',
  hrContract: '00000000-0000-7009-9100-000000000009',
  hrPayroll: '00000000-0000-7010-9100-000000000010',
  realEstateDeed: '00000000-0000-7011-9100-000000000011',
  realEstateRental: '00000000-0000-7012-9100-000000000012',
  // Originally authored under a creator persona; converted into
  // Official Growing Intelligence products. UUIDs preserved so any
  // existing Vault links / verifier URLs keep resolving.
  codeProof:          '00000000-0000-7100-9100-000000000001',
  ideaProof:          '00000000-0000-7100-9100-000000000002',
  freelanceContract:  '00000000-0000-7100-9100-000000000003',
  beautyProtocol:     '00000000-0000-7100-9100-000000000004',
  fitnessPlanProof:   '00000000-0000-7100-9100-000000000005',
  recipeCreator:      '00000000-0000-7100-9100-000000000006',
  healthDiary:        '00000000-0000-7100-9100-000000000007',
  // Video production recipes — signed AI pipelines composing OpenAI,
  // ElevenLabs, Stability AI, and Runway connectors.
  videoFromScript:    '00000000-0000-7200-9100-000000000001',
  promoVideo:         '00000000-0000-7200-9100-000000000002',
  explainerVideo:     '00000000-0000-7200-9100-000000000003',
  signedVoiceover:    '00000000-0000-7200-9100-000000000004',
  thumbnailGenerator: '00000000-0000-7200-9100-000000000005',
  // Capital markets recipes — R01–R12.
  cmTradeReconstruction:        '00000000-0000-7300-9100-000000000001',
  cmBestExecutionAttestation:   '00000000-0000-7300-9100-000000000002',
  cmMifirEmirCatTriReport:      '00000000-0000-7300-9100-000000000003',
  cmNavAttestation:             '00000000-0000-7300-9100-000000000004',
  cmModelLineageSnapshot:       '00000000-0000-7300-9100-000000000005',
  cmCommunicationEvidenceBundle:'00000000-0000-7300-9100-000000000006',
  cmSurveillanceAlertEvidence:  '00000000-0000-7300-9100-000000000007',
  cmCsdSettlementFailEvidence:  '00000000-0000-7300-9100-000000000008',
  cmDoraIctIncidentPack:        '00000000-0000-7300-9100-000000000009',
  cmIsaMagnaIxbrlFiling:        '00000000-0000-7300-9100-000000000010',
  cmTokenizedBondIssuance:      '00000000-0000-7300-9100-000000000011',
  cmProxyVoteReceipt:           '00000000-0000-7300-9100-000000000012',
  // Capital markets commercial flagships (CAPITAL-MARKETS-RESEARCH §8.6).
  // Enterprise-priced — `monthly_price_usd: null` suppresses the numeric
  // label so the marketplace card renders "Enterprise — Contact Us".
  cmComplianceVault:            '00000000-0000-7301-9100-000000000001',
  cmModelNavProvenance:         '00000000-0000-7301-9100-000000000002',
  cmTradeReconstructionSuite:   '00000000-0000-7301-9100-000000000003',
} as const;

/** All official product ids as a Set, for O(1) lookup in cards. */
export const OFFICIAL_PRODUCT_ID_SET: ReadonlySet<EntityId> = new Set(
  Object.values(OFFICIAL_PRODUCT_IDS),
);

/** Growing Intelligence Ltd.'s author key. Demo seed `0xA1×32` —
 *  matches `products/gate-to-shelf/manifest.json`. Production replaces
 *  with the GI Ltd. CA-issued key before signing. */
const GI_AUTHOR_PUBKEY: PublicKey =
  'ed25519:bc7cbcb5636375fa1d82434d466724d92377f53b980695dd49d26d0ce12205a5';

const PUBLISHED_AT_FIXED: TrustedTimestamp = {
  iso: '2026-04-23T00:00:00Z',
  source: 'RFC3161',
};

/** Per-product display strings keyed by locale. */
interface ProductCopy {
  name: string;
  tagline: string;
  description: string;
  starter_template: string;
  /** Phase 4.9 — rich Overview content for the MarketplaceDetailPage.
   *  Optional because not every official product has had its long-form
   *  pain/solution/how-it-works copy authored yet. */
  overview?: ProductOverview;
}

/** Long-form Overview content rendered on `MarketplaceDetailPage`'s
 *  Overview tab when the listing is one of the 6 Official products. */
export interface ProductOverview {
  /** What breaks today, with a concrete consequence. 2-3 sentences. */
  pain: string;
  /** How this product flips the situation. 2-3 sentences. */
  solution: string;
  /** Three or four step labels. Renders as a numbered flow. */
  how_it_works: string[];
  /** Sentence-form audience description. */
  who_for: string;
  /** Demo persona slug — links to `/demo?persona=<key>`. Omit for
   *  products that don't have a matching demo tab (e.g. Legal Evidence). */
  demo_persona?: string;
}

interface OfficialProduct {
  /** Stable display id. */
  root_id: EntityId;
  /** Slug from `products/<slug>/`. Used by the "Use this product"
   *  Spark prefill to namespace the new GI. */
  slug: string;
  /** Engine `DomainType`. Standard variants render with branded
   *  styling; non-standard ones (Cyber, Education, Legal) wrap in a
   *  `{ Custom }` envelope and fall through to the generic palette. */
  domain: DomainType;
  /**
   * Annual-contract price in whole USD per month. All prices are USD.
   * `null` for `enterprise: true` flagships — the card renders
   * "Enterprise — Contact Us" instead of a numeric label.
   */
  price_usd: number | null;
  /**
   * Monthly (no-contract) price in whole USD. Convention is annual
   * + 30 % so customers see the value of locking in a year. `null`
   * for enterprise flagships.
   */
  monthly_price_usd: number | null;
  contracts_count: number;
  inputs: PortDefinition[];
  outputs: PortDefinition[];
  copy: Record<Locale, ProductCopy>;
  /**
   * When `true`, the card renders "Enterprise — Contact Us" + a
   * "Contact Sales" mailto CTA instead of a numeric price + Install
   * button. Used for the three Capital-Markets flagships
   * (cm-compliance-vault, cm-trade-reconstruction-suite,
   * cm-model-nav-provenance). Per CAPITAL-MARKETS-RESEARCH §8.6.
   */
  enterprise?: boolean;
}

const OFFICIAL_PRODUCTS: ReadonlyArray<OfficialProduct> = [
  {
    root_id: OFFICIAL_PRODUCT_IDS.gateToShelf as EntityId,
    slug: 'gate-to-shelf',
    domain: 'Process' as DomainType,
    price_usd: 5000,
    monthly_price_usd: 6500,
    contracts_count: 3,
    inputs: [{ name: 'gate_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'attestation', data_type: 'Stream', required: true }],
    copy: {
      en: {
        name: 'Gate to Shelf™',
        tagline: 'Supplier-to-shelf compliance, signed at every stage.',
        description:
          'End-to-end supplier-to-shelf compliance for food manufacturers. ' +
          'Covers gate inspection, production-line monitoring, and retail ' +
          'shelf verification. Every stage emits a cryptographically signed ' +
          'attestation that satisfies FSSC 22000 traceability, Israeli Food ' +
          'Law, Amendment 13 labelling, and SHAAM allocation records.',
        starter_template:
          'Three signed contracts compose into one Conductor: ' +
          'gate-inspector → line-monitor → shelf-verifier. ' +
          'Regulator audits resolve in minutes instead of weeks.',
        overview: {
          pain:
            'Food manufacturers get fined into the millions for batches no one can trace. Prigat ate ₪16M+ in penalties when the chain broke between supplier intake and shelf. The QA lead spends three days reconstructing what should have been a one-click chain — and the answer they reconstruct is the answer the regulator decides to disbelieve.',
          solution:
            'Every batch signed at production. Every supplier check signed by the inspector who ran it. Every cold-chain reading signed by the sensor that emitted it. Hand the regulator a verified chain — not a binder — and finish in minutes what used to take a week of war room.',
          how_it_works: [
            'Sensors and inspectors push readings + photos straight into a signed contract event at the moment of action.',
            'Each event carries the regulation it satisfies (FSSC 22000, IFS Food, retailer specific) — late events escalate automatically.',
            'A regulator asks for the file; you produce a signed bundle in under a minute, reviewable offline.',
          ],
          who_for:
            'Food + beverage manufacturers, multi-site processors, FSSC 22000 / IFS Food shops, and any retailer-mandated cold-chain operator (dairy, meat, pharma-adjacent).',
          demo_persona: 'gate-to-shelf',
        },
      },
      he: {
        name: 'Gate to Shelf™',
        tagline: 'ציות מספק-עד-מדף — חתום בכל שלב.',
        description:
          'ציות מקצה-לקצה ליצרני מזון, מהשער ועד למדף הקמעונאי. כולל בדיקות ' +
          'בשער, ניטור פסי ייצור ואימות מדף. כל שלב מנפיק אישור חתום ' +
          'קריפטוגרפית התואם לתקני FSSC 22000, חוק המזון, תיקון 13 לסימון, ' +
          'ורישומי הקצאות שע"מ.',
        starter_template:
          'שלושה חוזים חתומים מורכבים בקונדקטור אחד: ' +
          'gate-inspector → line-monitor → shelf-verifier. ' +
          'ביקורות רגולטור נסגרות תוך דקות במקום שבועות.',
        overview: {
          pain:
            'יצרני מזון משלמים קנסות במיליונים על אצוות שאי אפשר לעקוב אחריהן. פריגת ספגה יותר מ-16 מיליון ₪ כשהשרשרת נשברה בין קליטת ספק למדף. אחראי האיכות מבלה שלושה ימים לשחזר מה שהיה אמור להיות שרשרת בלחיצת כפתור — והשחזור הוא בדיוק מה שהרגולטור בוחר לא להאמין לו.',
          solution:
            'כל אצווה נחתמת בייצור. כל בדיקת ספק נחתמת על-ידי הבודק שביצע אותה. כל קריאת שרשרת קור נחתמת על-ידי החיישן שהפיק אותה. אתה מוסר לרגולטור שרשרת מאומתת — לא קלסר — ומסיים בדקות את מה שדרש שבוע של חדר מלחמה.',
          how_it_works: [
            'חיישנים ובודקים מזרימים קריאות וצילומים ישירות אל אירוע חוזה חתום ברגע הפעולה.',
            'כל אירוע נושא את הרגולציה שהוא מקיים (FSSC 22000, IFS Food, דרישות קמעונאי) — אירועים מאחרים מסלימים אוטומטית.',
            'רגולטור מבקש את התיק; אתה מפיק חבילה חתומה בפחות מדקה, ניתנת לאימות לא-מקוון.',
          ],
          who_for:
            'יצרני מזון ומשקאות, מפעלי ריבוי-אתר, ארגוני FSSC 22000 / IFS Food, וכל מפעיל שרשרת קור שדורש קמעונאי (חלב, בשר, פארמה-סמוך).',
          demo_persona: 'gate-to-shelf',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.amarReporter as EntityId,
    slug: 'amar-reporter',
    domain: 'Medical' as DomainType,
    price_usd: 10000,
    monthly_price_usd: 13000,
    contracts_count: 2,
    inputs: [{ name: 'incident', data_type: 'Document', required: true }],
    outputs: [{ name: 'amar_report', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'AMAR Reporter™',
        tagline: 'Event → signed report → AMAR in minutes.',
        description:
          'Turns medical device incidents into AMAR-compliant, ' +
          'cryptographically signed reports inside the 48-hour window. ' +
          'Ingests the incident in seconds, generates the regulator ' +
          'submission in minutes, and produces an audit trail no inspector ' +
          'can contest.',
        starter_template:
          'Pre-wired for Israeli Ministry of Health AMAR submissions. ' +
          'Two signed contracts: incident-classifier and amar-builder. ' +
          'Hebrew + English regulator-ready exports.',
        overview: {
          pain:
            'A medical device incident happens at 23:14 on Friday. The Israeli AMAR clock starts at the moment the clinician knew. By Monday morning the regulator wants the full report — and your team is reconstructing timestamps from email threads and shift logs. Late or sloppy reports trigger ₪8M+ fines and a Health Ministry follow-up your QA team will not survive.',
          solution:
            'Incident detected → AMAR-format report auto-generated → signed under the device UDI and the clinician\'s identity → submitted with cryptographic proof of timeline. The 48-hour clock runs against signed evidence, not memory. Your team writes the medical narrative; the system handles the chain.',
          how_it_works: [
            'Clinician or QA engineer logs the incident; the system signs it with the device UDI, clinician identity, and a trusted timestamp.',
            'AMAR fields auto-fill (symptoms, root cause, corrective action) from the device\'s prior state; clinician reviews and co-signs.',
            'Submission bundle ships to the Health Ministry with cryptographic provenance the regulator can verify offline.',
          ],
          who_for:
            'Medical device manufacturers, multi-site hospital networks, IEC 62304 / ISO 14971 software/hardware shops, and any importer subject to Israeli Medical Devices Law §12.',
          demo_persona: 'amar-reporter',
        },
      },
      he: {
        name: 'מערכת דיווח AMAR™',
        tagline: 'אירוע → דוח חתום → AMAR תוך דקות.',
        description:
          'הופך אירועי ציוד רפואי לדיווחי AMAR חתומים קריפטוגרפית — בתוך ' +
          'חלון 48 השעות. קולט את האירוע בשניות, מייצר את הגשת הרגולטור ' +
          'תוך דקות, ומפיק שרשרת ראיות שאף בודק לא יוכל לערער עליה.',
        starter_template:
          'מחוברת מראש לטופס AMAR של משרד הבריאות. שני חוזים חתומים: ' +
          'incident-classifier ו-amar-builder. ייצוא דו-לשוני (עברית + ' +
          'אנגלית) מוכן לרגולטור.',
        overview: {
          pain:
            'אירוע ציוד רפואי מתרחש ב-23:14 ביום שישי. שעון ה-AMAR הישראלי מתחיל ברגע שהקלינאי ידע. עד יום שני בבוקר הרגולטור רוצה דוח מלא — והצוות שלך משחזר חותמות זמן מהודעות אימייל ויומני משמרת. דוחות מאוחרים או רשלניים מובילים לקנסות מעל 8 מיליון ₪ ולמעקב של משרד הבריאות שצוות האיכות שלך לא ישרוד.',
          solution:
            'אירוע זוהה → דוח בפורמט AMAR מיוצר אוטומטית → נחתם תחת ה-UDI של המכשיר וזהות הקלינאי → מוגש עם הוכחת ציר זמן קריפטוגרפית. שעון 48 השעות רץ מול ראיות חתומות, לא מול זיכרון. הצוות שלך כותב את הנרטיב הרפואי; המערכת מטפלת בשרשרת.',
          how_it_works: [
            'הקלינאי או מהנדס איכות מתעד את האירוע; המערכת חותמת אותו עם UDI, זהות קלינאי, וחותמת זמן מהימנה.',
            'שדות AMAR ממולאים אוטומטית (תסמינים, שורש הבעיה, פעולה מתקנת) מהמצב הקודם של המכשיר; הקלינאי סוקר וחותם בנוסף.',
            'חבילת ההגשה נשלחת למשרד הבריאות עם פרובננס קריפטוגרפי שהרגולטור יכול לאמת לא-מקוון.',
          ],
          who_for:
            'יצרני ציוד רפואי, רשתות בתי חולים רב-אתריות, ארגוני IEC 62304 / ISO 14971 (חומרה ותוכנה), וכל יבואן הכפוף לחוק הציוד הרפואי הישראלי, סעיף 12.',
          demo_persona: 'amar-reporter',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.researchShield as EntityId,
    slug: 'research-shield',
    domain: { Custom: 'Education' },
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 3,
    inputs: [{ name: 'contribution', data_type: 'Document', required: true }],
    outputs: [{ name: 'integrity_certificate', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Research Shield™',
        tagline: 'Prove every contribution.',
        description:
          'Cryptographic proof of authorship, contribution weight, and ' +
          'timeline integrity for academic research. Every contribution to ' +
          'a paper, dataset, or protocol is signed by its contributor under ' +
          'their ORCID identity, weighted, and chained to the final ' +
          'submission so the resulting Research Integrity Certificate ' +
          'survives any plagiarism dispute, grant-agency audit, or ' +
          'authorship challenge.',
        starter_template:
          'ORCID-bound contributor signing, weighted contribution graph, ' +
          'and a Research Integrity Certificate at submission. Three signed ' +
          'contracts. Pre-configured for Ruppin, BGU, and Technion partners.',
        overview: {
          pain:
            'A peer-reviewer leaks the dataset. A co-author publishes the figure under their name. A student claims plagiarism the supervisor cannot disprove. AI rewrites blur authorship further every semester — and the dating fight is your word against theirs.',
          solution:
            'Every submission, every dataset, every figure signed at the moment it\'s uploaded. ORCID-bound; weighted by contribution graph; chained to the final paper so the resulting Integrity Certificate survives any plagiarism dispute, grant-agency audit, or authorship challenge years later.',
          how_it_works: [
            'Drop a dataset, notebook, or figure — the engine signs the SHA-256 + your ORCID-bound key in seconds.',
            'Each derived artifact references its parents — the chain itself is the proof of method.',
            'At submission the system emits a Research Integrity Certificate the journal editor verifies offline.',
          ],
          who_for:
            'Universities, research institutes, principal investigators defending priority, departments preparing for grant-agency audits, and any lab that needs to prove "we had this first" against a future challenge.',
          demo_persona: 'research-shield',
        },
      },
      he: {
        name: 'Research Shield™',
        tagline: 'תוכיח כל תרומה למחקר.',
        description:
          'הוכחה קריפטוגרפית של זכויות יוצרים, משקל תרומה ושלמות ציר הזמן ' +
          'למחקר אקדמי. כל תרומה למאמר, מאגר נתונים או פרוטוקול נחתמת על ' +
          'ידי המשתתף תחת זהות ORCID שלו, משוקללת ומשורשרת להגשה הסופית — ' +
          'כך שתעודת שלמות המחקר שורדת כל מחלוקת על העתקה, ביקורת של ' +
          'גוף מימון, או אתגר על זכויות יוצרים.',
        starter_template:
          'חתימת תורם מאומתת ב-ORCID, גרף תרומות משוקלל, ותעודת שלמות ' +
          'מחקר בעת הגשה. שלושה חוזים חתומים. מוגדרים מראש לשותפי רופין, ' +
          'בן-גוריון, והטכניון.',
        overview: {
          pain:
            'Peer-reviewer מדליף את הדאטה-סט. שותף-מחבר מפרסם את האיור בשמו. סטודנט טוען לפלגיאט שהמנחה לא יכול להפריך. AI מטשטש את שאלת הזכויות מסמסטר לסמסטר — והקרב על תיעוד הזמן הוא המילה שלך מול המילה שלהם.',
          solution:
            'כל הגשה, כל דאטה-סט, כל איור — חתומים ברגע ההעלאה. מאומתים ב-ORCID; משוקללים לפי גרף תרומות; משורשרים לעבודה הסופית כך שתעודת שלמות המחקר שורדת כל מחלוקת על העתקה, ביקורת של גוף מימון, או אתגר על זכויות יוצרים שנים אחרי.',
          how_it_works: [
            'מטילים דאטה-סט, מחברת או איור — המנוע חותם את ה-SHA-256 + המפתח המאומת ב-ORCID תוך שניות.',
            'כל ארטיפקט נגזר מצביע להוריו — השרשרת עצמה היא ההוכחה לשיטה.',
            'בהגשה המערכת מנפיקה תעודת שלמות מחקר שעורך כתב-העת מאמת לא-מקוון.',
          ],
          who_for:
            'אוניברסיטאות, מכוני מחקר, חוקרים ראשיים שמגינים על קדימות, מחלקות שמכינות ביקורת לגוף מימון, וכל מעבדה שצריכה להוכיח "יצרנו את זה ראשונים" מול אתגר עתידי.',
          demo_persona: 'research-shield',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.citizenMirror as EntityId,
    slug: 'citizen-mirror',
    domain: 'Government' as DomainType,
    price_usd: 3000,
    monthly_price_usd: 3900,
    contracts_count: 4,
    inputs: [{ name: 'citizen_request', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_response', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Citizen Mirror™',
        tagline: 'Every request, every answer, proven.',
        description:
          'Municipal citizen-services proof layer. Every citizen request is ' +
          'logged with a signed receipt; every response is hash-bound to the ' +
          'original request; SLA breaches escalate automatically. Produces ' +
          'State-Comptroller-ready exports in Hebrew and English. 99.5% ' +
          'engine availability under the signed SLO.',
        starter_template:
          'Pre-wired for Israeli municipal workflows: request intake, SLA ' +
          'monitor, escalation, and Comptroller-format exports. Four signed ' +
          'contracts. Up to 10,000 requests/month at the listed price.',
        overview: {
          pain:
            '₪18M of unfulfilled service orders. Zero FOIA reports filed in 2024. 43 lawsuits over two years from residents whose requests vanished. The State Comptroller is asking for the file you reconstruct overnight in panic — and the Comptroller decides which version to believe.',
          solution:
            'Every council decision, every citizen request, every procurement action signed at the moment it happens. Public tracking ID per request; SLA clock running against signed evidence; auto-escalation in five steps that ends at the Comptroller\'s desk with their own export schema pre-packaged.',
          how_it_works: [
            'Citizen submits via web, phone, or counter — a signed receipt issues within one hour with tracking ID and statutory deadline.',
            'Per-category SLA runs: 20 days FOIA, 5 service, 14 complaints, 24h emergency. Late steps escalate automatically.',
            'Comptroller asks for the file; you hand over a verified chain — same day.',
          ],
          who_for:
            'Mid-sized Israeli municipalities (40k–150k residents), regional councils, transparency offices, director-generals who do not want to take a call from the State Comptroller\'s office.',
          demo_persona: 'citizen-mirror',
        },
      },
      he: {
        name: 'מראה אזרחית™',
        tagline: 'כל בקשה, כל תשובה — מוכחת.',
        description:
          'שכבת הוכחה לשירותי האזרח ברשות מקומית. כל בקשת אזרח מתועדת עם ' +
          'אישור חתום; כל מענה מקושר בהאש לבקשה המקורית; הפרת SLA מסלימה ' +
          'אוטומטית. מפיקה ייצוא מוכן למבקר המדינה בעברית ובאנגלית. 99.5% ' +
          'זמינות תחת SLO חתום.',
        starter_template:
          'מחוברת מראש לתהליכי רשות מקומית: קליטת בקשה, מעקב SLA, הסלמה, ' +
          'וייצוא בפורמט מבקר המדינה. ארבעה חוזים חתומים. עד 10,000 ' +
          'בקשות/חודש במחיר הנקוב.',
        overview: {
          pain:
            '₪18 מיליון של הזמנות שירות לא ממומשות. אפס דוחות חופש מידע שהוגשו ב-2024. 43 תביעות בשנתיים מתושבים שבקשותיהם נעלמו. מבקר המדינה מבקש את התיק שאתה משחזר בלילה בפאניקה — והמבקר מחליט באיזו גרסה להאמין.',
          solution:
            'כל החלטת מועצה, כל בקשת תושב, כל פעולת רכש — נחתמת ברגע שהיא מתרחשת. מזהה מעקב ציבורי לכל בקשה; שעון SLA רץ מול ראיות חתומות; הסלמה אוטומטית בחמישה שלבים שמסתיימת על שולחן המבקר עם סכמת ייצוא משלו, ארוזה מראש.',
          how_it_works: [
            'תושב מגיש דרך אינטרנט, טלפון או דלפק — אישור חתום מונפק תוך שעה עם מזהה מעקב ודד-ליין סטטוטורי.',
            'SLA רץ לפי קטגוריה: 20 ימים לחופש מידע, 5 לשירות, 14 לתלונות, 24 שעות לחירום. שלבים מאחרים מסלימים אוטומטית.',
            'המבקר מבקש את התיק; אתה מוסר שרשרת מאומתת — באותו יום.',
          ],
          who_for:
            'רשויות מקומיות בינוניות בישראל (40-150 אלף תושבים), מועצות אזוריות, אגפי שקיפות, ומנכ"לים שלא רוצים לקבל שיחה ממשרד מבקר המדינה.',
          demo_persona: 'citizen-mirror',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cyberShield as EntityId,
    slug: 'cyber-shield',
    domain: { Custom: 'Cyber' },
    price_usd: 16000,
    monthly_price_usd: 20800,
    contracts_count: 5,
    inputs: [{ name: 'detection', data_type: 'Document', required: true }],
    outputs: [{ name: 'evidence_package', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Cyber Shield™',
        tagline: 'Every threat, proven.',
        description:
          'Cryptographic proof for the full incident lifecycle — from first ' +
          'IOC through containment, eradication, recovery, and post-incident ' +
          'review. Every detection is Ed25519-signed under the analyst\'s ' +
          'identity and carries an RFC 3161 trusted timestamp; every piece ' +
          'of forensic evidence is hash-bound to a signed chain-of-custody ' +
          'record. Survives NIST SP 800-61, NIST SP 800-86, and SWGDE review.',
        starter_template:
          'NIST SP 800-61 phase mapping built in. Five signed contracts: ' +
          'detect → contain → eradicate → recover → review. ' +
          'Court-admissible Incident Evidence Package per case.',
        overview: {
          pain:
            'A breach hits at 03:42. By morning the SOC has Slack screenshots, a ticket queue, and three different timelines. Six weeks later the NIST 800-61 audit asks for the chain of custody on each indicator of compromise. Half is reconstructed; the other half is gone. Forensic evidence gets thrown out of court because the chain-of-custody cannot be proven, and the negligent-response lawsuit is now unwinnable.',
          solution:
            'Every alert, every IOC, every analyst sign-off, every containment action signed at the moment with the analyst-of-record\'s key and an RFC 3161 trusted timestamp. NIST SP 800-61 phase mapping built in. Pass the audit cold; defend the response in court if it ever has to be.',
          how_it_works: [
            'Detection event → analyst sign-off → containment action — each entry an Ed25519-signed contract event with trusted timestamp.',
            'Every escalation links to the parent event; the playbook walked is the chain you can prove.',
            'Audit requests pre-populate NIST 800-61 / ISO 27035 / NIS2 export schemas; the auditor verifies offline.',
          ],
          who_for:
            'SOC teams, CISO offices, MSSPs, regulated enterprises (banking, telco, healthcare) under NIS2 / Israeli Cyber Defence Directorate reporting, and anyone whose forensic evidence has to survive a Daubert hearing.',
          demo_persona: 'cyber-shield',
        },
      },
      he: {
        name: 'Cyber Shield™',
        tagline: 'כל איום — מוכח.',
        description:
          'הוכחה קריפטוגרפית למחזור החיים המלא של אירוע סייבר — מה-IOC ' +
          'הראשון, דרך בלימה, מיגור, התאוששות וסקירה. כל זיהוי חתום ב-Ed25519 ' +
          'תחת זהות האנליסט, ונושא חותמת זמן מהימנה לפי RFC 3161; כל פריט ' +
          'ראיות פורנזי מקושר בהאש לרשומת שרשרת משמורת חתומה. שורד ביקורת ' +
          'NIST SP 800-61, NIST SP 800-86, ו-SWGDE.',
        starter_template:
          'מיפוי שלבי NIST SP 800-61 מובנה. חמישה חוזים חתומים: ' +
          'זיהוי → בלימה → מיגור → התאוששות → סקירה. ' +
          'חבילת ראיות אירוע קבילה בבית משפט לכל תיק.',
        overview: {
          pain:
            'פריצה ב-03:42. עד הבוקר ל-SOC יש צילומי מסך מ-Slack, תור tickets, ושלושה צירי זמן שונים. שישה שבועות אחרי, ביקורת NIST 800-61 שואלת לשרשרת המשמורת על כל IOC. חצי משוחזר; חצי אבד. ראיות פורנזיות נדחות בבית משפט כי שרשרת המשמורת לא מוכחת — ותביעת רשלנות התגובה הופכת ללא נצחה.',
          solution:
            'כל התראה, כל IOC, כל אישור אנליסט, כל פעולת בלימה — נחתמים ברגע עם המפתח של האנליסט-של-רשומה ועם חותמת זמן מהימנה לפי RFC 3161. מיפוי שלבי NIST SP 800-61 מובנה. עוברים ביקורת בקור רוח; מגינים על התגובה בבית משפט אם צריך.',
          how_it_works: [
            'אירוע זיהוי → אישור אנליסט → פעולת בלימה — כל רשומה היא אירוע חוזה חתום ב-Ed25519 עם חותמת זמן מהימנה.',
            'כל הסלמה מקושרת לאירוע ההורה; ה-playbook שצעדת בו הוא השרשרת שאתה יכול להוכיח.',
            'בקשות ביקורת ממלאות מראש סכמות ייצוא NIST 800-61 / ISO 27035 / NIS2; הבודק מאמת לא-מקוון.',
          ],
          who_for:
            'צוותי SOC, אגפי CISO, MSSPs, ארגונים מפוקחים (בנקאות, טלקום, בריאות) תחת דיווח NIS2 / מערך הסייבר הלאומי, וכל מי שראיותיו הפורנזיות חייבות לשרוד שימוע Daubert.',
          demo_persona: 'cyber-shield',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.legalEvidence as EntityId,
    slug: 'legal-evidence',
    domain: { Custom: 'Legal' },
    price_usd: 8000,
    monthly_price_usd: 10400,
    contracts_count: 3,
    inputs: [{ name: 'evidence_item', data_type: 'Document', required: true }],
    outputs: [{ name: 'court_package', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Legal Evidence™',
        tagline: 'Court-ready, chain-proven.',
        description:
          'Court-admissible evidence packages with complete chain-of-custody ' +
          'tracking. Every evidence item is hashed at intake, every handoff ' +
          'is signed, and every package ships with FRE 902(13) and 902(14) ' +
          'self-authenticating certifications by a qualified engine attestor. ' +
          'Bilingual exports for Israeli and US litigation contexts.',
        starter_template:
          'FRE 902(13) + 902(14) compliant. Three signed contracts: ' +
          'intake → custody → certification. Up to 200 evidence items ' +
          'per month at the listed price; unlimited handoffs within tracked ' +
          'items.',
        overview: {
          pain:
            'Evidence challenged in court. No proof of when the document existed, who held it, or whether anyone touched it between 13:15 on the 24th and 11:40 on the 29th. Opposing counsel asks the question; the answer is silence; the exhibit is out — and a year of investigation goes with it.',
          solution:
            'Every document signed at creation. Every handoff dual-signed by releasing AND receiving party. Every package ships with FRE 902(13) and 902(14) self-authenticating certifications by a qualified engine attestor. The chain that walks into court is the chain that was actually maintained.',
          how_it_works: [
            'Capture evidence with the five W\'s as required fields; SHA-256 of the original is bound to the contract event.',
            'Each handoff signed by both releasing and receiving custodian — single-signature handoffs refused before they hit the file.',
            'At case-prep the verifier walks the chain end-to-end and produces a court-ready bundle, or a structured "broken at step 4" refusal you can fix today, not at the hearing.',
          ],
          who_for:
            'Litigation boutiques, corporate legal departments, compliance teams running internal investigations, licensed private investigators, and any forensic consultant whose methodology has to survive Daubert.',
        },
      },
      he: {
        name: 'ראיה משפטית™',
        tagline: 'מוכן לבית המשפט. שרשרת הוכחה ללא פערים.',
        description:
          'חבילות ראיות קבילות בבית משפט עם מעקב שרשרת משמורת מלא. כל ' +
          'פריט ראיות מקבל האש בקליטה, כל העברת אחריות נחתמת, וכל חבילה ' +
          'מגיעה עם תעודות אימות עצמי לפי FRE 902(13) ו-902(14) מ-attestor ' +
          'מוסמך של המנוע. ייצוא דו-לשוני להליכים בישראל ובארה"ב.',
        starter_template:
          'תואם FRE 902(13) + 902(14). שלושה חוזים חתומים: ' +
          'קליטה → משמורת → הסמכה. עד 200 פריטי ראיות לחודש במחיר הנקוב; ' +
          'העברות אחריות בלתי מוגבלות בתוך פריטים במעקב.',
        overview: {
          pain:
            'ראיות מאותגרות בבית משפט. אין הוכחה מתי המסמך התקיים, מי החזיק בו, או אם מישהו נגע בו בין 13:15 ב-24 ל-11:40 ב-29. הצד שכנגד שואל; התשובה היא שתיקה; המוצג נפסל — ושנה של חקירה הולכת איתו.',
          solution:
            'כל מסמך נחתם בעת היצירה. כל העברה נחתמת בחתימה כפולה — צד מוסר וצד מקבל. כל חבילה נשלחת עם תעודות אימות עצמי לפי FRE 902(13) ו-902(14) מ-attestor מוסמך של המנוע. השרשרת שנכנסת לבית המשפט היא השרשרת שבאמת נשמרה.',
          how_it_works: [
            'קולטים את הראייה עם 5 ה-W כשדות חובה; SHA-256 של המקור מקובע לאירוע החוזה.',
            'כל העברת אחריות נחתמת ע"י המוסר וגם ע"י המקבל — העברות חד-חתימה נדחות לפני שהן נכנסות לתיק.',
            'בהכנת התיק המאמת צועד בשרשרת מקצה-לקצה ומפיק חבילה מוכנה לבית-משפט, או סירוב מובנה ("נשבר בשלב 4") שאפשר לתקן היום, לא בדיון.',
          ],
          who_for:
            'משרדי ליטיגציה בוטיקיים, מחלקות משפטיות בארגונים, צוותי ציות שמנהלים חקירות פנימיות, חוקרים פרטיים מורשים, וכל יועץ פורנזי שהמתודולוגיה שלו צריכה לשרוד Daubert.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.constructionPermit as EntityId,
    slug: 'construction-permit',
    domain: 'Process' as DomainType,
    price_usd: 3500,
    monthly_price_usd: 4550,
    contracts_count: 2,
    inputs: [{ name: 'permit_record', data_type: 'Document', required: true }],
    outputs: [{ name: 'permit_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Construction Permit™',
        tagline: 'Every permit, every milestone — signed and defensible.',
        description:
          'End-to-end building permit validation for Israeli construction projects. ' +
          'Validates permit number format, contractor licensing, permit expiry, and ' +
          'safety plan presence against the Planning and Building Law. Every permit ' +
          'and milestone event is cryptographically signed — giving contractors, ' +
          'inspectors, and municipalities a verifiable chain from permit issuance ' +
          'through first milestone completion.',
        starter_template:
          'Two signed contracts compose into one Conductor: ' +
          'permit-validator → milestone-tracker. ' +
          'Enforcement officers resolve a single hash and see the full permit chain.',
        overview: {
          pain:
            'Israeli construction pays hundreds of millions of shekels a year in fines and demolition orders, and the chain breaks at the same place: there is no signed proof from permit issuance through each construction milestone. The defence file is a PDF from the municipality, a WhatsApp from the site manager, and a spreadsheet nobody touched since the permit arrived. An enforcement officer walks in and within an hour you are pricing the demolition.',
          solution:
            'Every permit signed at issuance under contractor licence + safety-plan hash. Every milestone — foundation, structure, roofing, final — chains off the permit with inspector credential, photo hash, and completion percentage. The municipal officer resolves a single hash and walks the project history unbroken.',
          how_it_works: [
            'Permit submitted — number format, contractor licence (IL-CON-*), expiry, and safety-plan hash validated and signed in one event.',
            'Each milestone signed by the inspector with photo evidence; the chain references the parent permit hash so nothing dangles.',
            'Enforcement officer asks for the file; you produce one hash, the chain verifies offline in under a minute.',
          ],
          who_for:
            'Israeli general contractors, developers, and municipalities managing active building permits under the Planning and Building Law — especially anyone fined or hit with an enforcement order in the past two years.',
        },
      },
      he: {
        name: 'Construction Permit™',
        tagline: 'כל היתר, כל מיילסטון — חתום וניתן להגנה.',
        description:
          'אימות היתרי בנייה מקצה לקצה לפרויקטי בנייה ישראלים. ' +
          'מאמת פורמט מספר ההיתר, רישיון קבלן, תוקף ההיתר, ונוכחות תכנית בטיחות ' +
          'לפי חוק התכנון והבנייה. כל היתר ומיילסטון חתום קריפטוגרפית — ' +
          'נותן לקבלנים, מפקחים ורשויות שרשרת מאומתת מהיתר ועד מיילסטון ראשון.',
        starter_template:
          'שני חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'permit-validator → milestone-tracker. ' +
          'מפקחי אכיפה פותחים hash אחד ורואים את שרשרת ההיתר המלאה.',
        overview: {
          pain:
            'ענף הבנייה בישראל משלם מאות מיליוני שקלים בשנה על קנסות וצווי הריסה, והשרשרת נשברת באותו מקום: אין הוכחה חתומה מהיתר הבנייה ועד כל שלב של הפרויקט. תיק ההגנה הוא PDF מהוועדה, הודעת וואטסאפ ממנהל האתר, וגיליון אקסל שאף אחד לא עדכן מאז שהגיע ההיתר. מפקח נכנס ובתוך שעה אתם מחשבים כמה תעלה ההריסה.',
          solution:
            'כל היתר נחתם בהנפקה תחת רישיון הקבלן + hash תכנית הבטיחות. כל מיילסטון — יסודות, שלד, גג, גמר — משורשר ל-hash ההיתר עם חתימת מפקח, hash תמונות ואחוז ביצוע. המפקח העירוני פותח hash אחד ורואה את היסטוריית הפרויקט המלאה.',
          how_it_works: [
            'הגשת היתר — פורמט מספר, רישיון קבלן (IL-CON-*), תוקף, ו-hash תכנית בטיחות מאומתים ונחתמים באירוע אחד.',
            'כל מיילסטון נחתם ע״י המפקח עם hash תמונות; השרשרת מצביעה ל-hash ההיתר כך שכלום לא תלוי באוויר.',
            'מפקח אכיפה מבקש את התיק; אתם מספקים hash אחד, והשרשרת מתאמתת לא־מקוון בפחות מדקה.',
          ],
          who_for:
            'קבלנים ראשיים, יזמים ורשויות מקומיות בישראל שמנהלים היתרי בנייה פעילים תחת חוק התכנון והבנייה — במיוחד מי שספג קנס או צו אכיפה בשנתיים האחרונות.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.constructionInspection as EntityId,
    slug: 'construction-inspection',
    domain: 'Process' as DomainType,
    price_usd: 3000,
    monthly_price_usd: 3900,
    contracts_count: 2,
    inputs: [{ name: 'inspection_report', data_type: 'Document', required: true }],
    outputs: [{ name: 'inspection_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Construction Inspection™',
        tagline: 'Every site inspection, signed safe.',
        description:
          'Signed site-safety inspections for Israeli construction sites. ' +
          'Validates inspector certification, PPE compliance, structural material ' +
          'SII certifications, and inspection result. A failed inspection halts ' +
          'the site instantly; every deficiency and remediation closure creates ' +
          'a new signed entry in the same chain — the complete correction history ' +
          'that satisfies Labor Ministry reinspection requirements.',
        starter_template:
          'Two signed contracts compose into one Conductor: ' +
          'site-inspector → deficiency-tracker. ' +
          'A single hash gives the Labor Ministry the full inspection-to-closure chain.',
        overview: {
          pain:
            'The Labor Ministry has been ramping up site inspections — work-stop orders, fines up to ₪350,000, and criminal liability for site managers. The problem is not the inspections; it is the after. Deficiency lists live in WhatsApp groups, paper closure forms get lost, and the reinspection three weeks later turns into a fight over what was actually fixed and when.',
          solution:
            'Every inspection signed by the certified inspector at the moment it happens. A failed result halts the site within seconds and notifies the safety officer plus the Labor Ministry. Every deficiency closure becomes a new signed entry on the same chain — the full open-to-close history a Labour Court or Ministry enforcement officer reviews in one record.',
          how_it_works: [
            'Inspector arrives — certification, PPE compliance, SII material certs, and overall result are validated and signed in one event.',
            'Failed inspection → automatic site halt + safety-officer + Ministry notification, all chained off the same hash.',
            'Each remediation closure signed and chained; the Ministry inspector returns, opens the chain, and confirms in minutes.',
          ],
          who_for:
            'Israeli general contractors, subcontractors, and developers under periodic Labor Ministry inspections, multi-site operators, and any shop demonstrating OSHA / ISO 45001 compliance to international clients or insurers.',
        },
      },
      he: {
        name: 'Construction Inspection™',
        tagline: 'כל ביקורת אתר, חתומה בטוח.',
        description:
          'ביקורות בטיחות חתומות לאתרי בנייה ישראלים. ' +
          'מאמת הסמכת מפקח, ציוד מגן אישי, תעודות SII לחומרי בנייה, ותוצאת ביקורת. ' +
          'ביקורת כושלת עוצרת את האתר מיידית; כל ליקוי וסגירת תיקון יוצרים ' +
          'רשומה חתומה חדשה באותה שרשרת — היסטוריית תיקונים שלמה לדרישות ' +
          'הביקורת החוזרת של משרד העבודה.',
        starter_template:
          'שני חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'site-inspector → deficiency-tracker. ' +
          'hash אחד נותן למשרד העבודה את שרשרת הביקורת-עד-סגירה המלאה.',
        overview: {
          pain:
            'משרד העבודה מגביר את ביקורות הבטיחות באתרים בישראל — צווי עצירת עבודה, קנסות עד ₪350,000, ואחריות פלילית של מנהלי אתר. הבעיה אינה הביקורות; היא מה שקורה אחר כך. רשימות ליקויים יושבות בקבוצת וואטסאפ, טפסי סגירה אובדים, והביקורת החוזרת אחרי שלושה שבועות הופכת למאבק על מה תוקן ומתי.',
          solution:
            'כל ביקורת נחתמת ע״י המפקח המוסמך ברגע שהיא מתרחשת. תוצאת fail עוצרת את האתר תוך שניות ומודיעה לאחראי הבטיחות ולמפקח משרד העבודה. כל סגירת ליקוי הופכת לרשומה חתומה חדשה באותה שרשרת — היסטוריה שלמה של פתיחה-עד-סגירה שבית הדין או מפקח האכיפה רואים ברשומה אחת.',
          how_it_works: [
            'המפקח מגיע — הסמכה, ציוד מגן אישי, תעודות SII ותוצאה כוללת מאומתים ונחתמים באירוע אחד.',
            'ביקורת נכשלת → עצירת אתר אוטומטית + הודעה לאחראי בטיחות ולמשרד, הכול משורשר לאותו hash.',
            'כל סגירת ליקוי חתומה ומשורשרת; המפקח חוזר, פותח את השרשרת, ומאשר תוך דקות.',
          ],
          who_for:
            'קבלנים ראשיים, קבלני משנה ויזמים ישראלים תחת ביקורות תקופתיות של משרד העבודה, מפעילי ריבוי-אתרים, וכל ארגון שמוכיח עמידה ב-OSHA / ISO 45001 ללקוחות בינלאומיים או חברות ביטוח.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.hrContract as EntityId,
    slug: 'hr-contract',
    domain: 'Process' as DomainType,
    price_usd: 4000,
    monthly_price_usd: 5200,
    contracts_count: 2,
    inputs: [{ name: 'employment_contract', data_type: 'Document', required: true }],
    outputs: [{ name: 'compliance_certificate', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'HR Contract™',
        tagline: 'Every employment term, signed and defensible.',
        description:
          'Signed employment contract validation under Israeli labor law and GDPR. ' +
          'Validates contract type, salary floor (₪5,537/mo minimum), weekly hours ' +
          '(42 h/week maximum), GDPR Article 6 processing basis, and notice period. ' +
          'Every contract is signed at issuance — the authoritative proof of ' +
          'statutory compliance that resolves Labour Court wage disputes without ' +
          'opening a binder.',
        starter_template:
          'Two signed contracts compose into one Conductor: ' +
          'contract-validator → labor-compliance-checker. ' +
          'Labour Courts and GDPR supervisors get a verified chain at issuance.',
        overview: {
          pain:
            'Israeli Labour Courts handle 120,000+ wage disputes a year, and almost all of them come down to one question: what did the parties actually agree, and when? The answer is usually a Word doc emailed to an HR inbox three years ago — by the time the judge asks, the document has been revised twice, the HR manager has left, and the employee\'s version has different numbers.',
          solution:
            'Every employment contract validated and signed at issuance. Type, gross salary, GDPR Article 6 basis, and notice period are checked; missing mandatory clauses or zero salary refuse the contract before either party signs. Labor compliance — minimum wage, 42 h/week max, statutory notice — chains off the contract hash. The chain in court is the chain that was actually signed.',
          how_it_works: [
            'Contract drafted — type, salary, GDPR basis, and notice period validated; non-conforming contracts are refused before signing.',
            'Labor compliance check chains off the contract hash: minimum-wage floor (₪5,537/mo, Apr 2026), 42 h/week ceiling, statutory notice period.',
            'Labour Court judge or GDPR supervisor asks for the file; you hand over a verified hash, the binder stays closed.',
          ],
          who_for:
            'Israeli employers issuing more than 10 contracts a year, anyone employing at or near the minimum wage, GDPR data processors needing documented Article 6 basis, and any employer recently in a Labour Court wage dispute.',
        },
      },
      he: {
        name: 'HR Contract™',
        tagline: 'כל תנאי העסקה, חתום וניתן להגנה.',
        description:
          'אימות חוזי העסקה חתומים לפי דיני עבודה ישראלים ו-GDPR. ' +
          'מאמת סוג חוזה, רצפת שכר (מינימום ₪5,537/חודש), שעות שבועיות ' +
          '(מקסימום 42 שעות/שבוע), בסיס עיבוד GDPR סעיף 6, ותקופת הסתגלות. ' +
          'כל חוזה חתום בהנפקה — הוכחה סמכותית לציות חוקי שפותרת תביעות שכר ' +
          'בבית הדין לעבודה ללא פתיחת קלסר.',
        starter_template:
          'שני חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'contract-validator → labor-compliance-checker. ' +
          'בתי הדין לעבודה ורגולטורי GDPR מקבלים שרשרת מאומתת בהנפקה.',
        overview: {
          pain:
            'בתי הדין לעבודה מטפלים בלמעלה מ-120,000 תביעות שכר בשנה, וכולן מגיעות לאותה שאלה: מה הצדדים באמת הסכימו, ומתי? התשובה כמעט תמיד היא מסמך Word שנשלח לתיבת HR לפני שלוש שנים — עד שהשופטת שואלת, המסמך עבר שתי עדכונות, מנהלת ה-HR עזבה, ולעובד יש מספרים אחרים.',
          solution:
            'כל חוזה העסקה מאומת ונחתם בהנפקה. סוג, שכר ברוטו, בסיס GDPR ותקופת הסתגלות נבדקים; סעיפי חובה חסרים או שכר אפס מסרבים לחוזה לפני שמישהו חותם. בדיקת ציות לדיני עבודה — שכר מינימום, 42 שעות/שבוע, תקופת הסתגלות — משורשרת ל-hash החוזה. השרשרת בבית הדין היא השרשרת שבאמת נחתמה.',
          how_it_works: [
            'חוזה נערך — סוג, שכר, בסיס GDPR ותקופת הסתגלות מאומתים; חוזים שאינם תואמים נדחים לפני חתימה.',
            'בדיקת ציות משורשרת ל-hash החוזה: רצפת שכר מינימום (₪5,537/חודש, אפריל 2026), תקרת 42 שעות/שבוע, תקופת הסתגלות סטטוטורית.',
            'שופט בית הדין או רגולטור GDPR מבקש את התיק; אתם מספקים hash מאומת, הקלסר נשאר סגור.',
          ],
          who_for:
            'מעסיקים ישראלים שמנפיקים יותר מ-10 חוזים בשנה, מי שמעסיק בשכר מינימום או קרוב, מעבדי מידע תחת GDPR שצריכים תיעוד סעיף 6, וכל מעסיק שהיה לאחרונה בסכסוך שכר בבית הדין לעבודה.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.hrPayroll as EntityId,
    slug: 'hr-payroll',
    domain: 'Process' as DomainType,
    price_usd: 4000,
    monthly_price_usd: 5200,
    contracts_count: 2,
    inputs: [{ name: 'payroll_record', data_type: 'Document', required: true }],
    outputs: [{ name: 'payslip_certificate', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'HR Payroll™',
        tagline: 'Every payslip, a signed proof of compliance.',
        description:
          'Cryptographically signed payroll proofs for Israeli employers. ' +
          'Validates minimum wage compliance, overtime pay, deduction cap (50% max), ' +
          'Bituach Leumi contributions (employee + employer), income tax withholding, ' +
          'and the employer\'s ITA deduction file number. Every payslip that passes ' +
          'is a tamper-proof record for the employee, employer, and Israel Tax Authority.',
        starter_template:
          'Two signed contracts compose into one Conductor: ' +
          'payroll-calculator → tax-compliance-checker. ' +
          'Wage Enforcement Authority audits resolve in minutes, not days.',
        overview: {
          pain:
            'Israeli employers pay ₪800M+ a year in back wages, interest, and Wage Enforcement Authority penalties. The cause is rarely intent — it is an inability to prove what was paid, when, and whether it complied with the law. A payslip is a piece of paper. A signed payslip is a legal shield.',
          solution:
            'Every payslip validated and signed at generation: minimum wage floor, overtime compensation, deduction cap (≤50%), Bituach Leumi contributions, income tax withholding, ITA deduction file number. A violation blocks the payslip before the employee receives it; a passed payslip is verified proof of statutory compliance for the employee, the employer, and the ITA.',
          how_it_works: [
            'Payroll calculated — gross above minimum wage (₪5,537/mo), overtime paid, deductions capped at 50% — or the slip is refused.',
            'Tax compliance chains off the payroll: Bituach Leumi (employee + employer), income tax withholding, ITA deduction file number all signed.',
            'Wage Enforcement audit — six months of payslips ship as a single signed bundle in under two minutes.',
          ],
          who_for:
            'Israeli employers with 20+ staff, anyone paying near the minimum wage, organisations recently audited by the Wage Enforcement Authority, and any payroll team that has had to defend practices to a Labour Court or the ITA.',
        },
      },
      he: {
        name: 'HR Payroll™',
        tagline: 'כל תלוש שכר, הוכחה חתומה לציות.',
        description:
          'הוכחות שכר חתומות קריפטוגרפית למעסיקים ישראלים. ' +
          'מאמת עמידה בשכר מינימום, תשלום שעות נוספות, תקרת ניכויים (מקסימום 50%), ' +
          'תשלומי ביטוח לאומי (עובד + מעסיק), ניכוי מס הכנסה, ' +
          'ומספר תיק ניכויים של המעסיק. כל תלוש שעובר הוא רשומה בלתי ניתנת לשינוי ' +
          'לעובד, למעסיק, ולרשות המסים.',
        starter_template:
          'שני חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'payroll-calculator → tax-compliance-checker. ' +
          'ביקורות רשות האכיפה נפתרות תוך דקות, לא ימים.',
        overview: {
          pain:
            'מעסיקים ישראלים משלמים יותר מ-₪800 מיליון בשנה בשכר עבר, ריבית וקנסות של רשות האכיפה. הסיבה אינה כוונה — היא חוסר יכולת להוכיח מה שולם, מתי, ואם זה היה חוקי. תלוש שכר הוא חתיכת נייר. תלוש שכר חתום הוא מגן משפטי.',
          solution:
            'כל תלוש שכר מאומת ונחתם בהפקה: רצפת שכר מינימום, תשלום שעות נוספות, תקרת ניכויים (≤50%), ביטוח לאומי, ניכוי מס הכנסה, מספר תיק ניכויים. הפרה חוסמת את התלוש לפני שהעובד מקבל אותו; תלוש שעובר הוא הוכחה מאומתת לציות חוקי לעובד, למעסיק, ולרשות המסים.',
          how_it_works: [
            'חישוב שכר — ברוטו מעל שכר מינימום (₪5,537/חודש), שעות נוספות שולמו, ניכויים בתקרת 50% — או התלוש נדחה.',
            'בדיקת מס משורשרת לשכר: ביטוח לאומי (עובד + מעסיק), ניכוי מס הכנסה, מספר תיק ניכויים — הכול חתום.',
            'ביקורת רשות האכיפה — שישה חודשי תלושים נשלחים כחבילה חתומה אחת תוך פחות משתי דקות.',
          ],
          who_for:
            'מעסיקים ישראלים עם 20+ עובדים, מי שמשלם קרוב לשכר מינימום, ארגונים שעברו לאחרונה ביקורת רשות האכיפה, וכל צוות שכר שנדרש להגן על נוהליו בבית הדין או ברשות המסים.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.realEstateDeed as EntityId,
    slug: 'realestate-deed',
    domain: 'Process' as DomainType,
    price_usd: 5000,
    monthly_price_usd: 6500,
    contracts_count: 2,
    inputs: [{ name: 'property_deed', data_type: 'Document', required: true }],
    outputs: [{ name: 'registration_packet', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Real Estate Deed™',
        tagline: 'Every transfer, Tabu-ready and signed.',
        description:
          'Signed property deed transfers for the Israeli Tabu land registry. ' +
          'Validates Tabu parcel identifier (gush-chelka format), buyer and seller ' +
          'identity, transaction value, and mas rechisha tax clearance. ' +
          'Produces a signed Land Registry submission packet — the timestamped proof ' +
          'of what was filed and when that protects both parties in any dispute.',
        starter_template:
          'Two signed contracts compose into one Conductor: ' +
          'deed-validator → tabu-registrar. ' +
          'Registration disputes resolve with a single hash — no binder, no fax.',
        overview: {
          pain:
            'Property transactions move millions of shekels on documents nobody can independently verify until something goes wrong. A missing mas rechisha clearance discovered three weeks after both sides thought the deal was done. A faxed Tabu submission with no record of what was sent. A competing lien filed between contract and registration — and the timestamp fight is your word against theirs.',
          solution:
            'Every deed validated at execution: gush-chelka format, buyer and seller identity, transaction value, mas rechisha clearance certificate. The Tabu submission packet is signed at filing — your proof of exactly what went in and when, long after the Tabu office processes the request.',
          how_it_works: [
            'Deed validated at execution — parcel id, parties, value, and mas rechisha clearance signed in one event; critical defects block the pipeline.',
            'Tabu submission packet signed at filing — conveyancer licence, form number, parcel consistency all chained off the deed hash.',
            'Competing lien or registration delay? Open the chain, show the timestamp on the clearance and the submission — the dispute resolves in a phone call.',
          ],
          who_for:
            'Israeli real estate lawyers, conveyancers, developers, and institutional investors handling 5+ property transfers a year — and anyone who has hit a registration delay caused by missing or unverifiable documentation.',
        },
      },
      he: {
        name: 'Real Estate Deed™',
        tagline: 'כל עסקה, מוכנה לטאבו וחתומה.',
        description:
          'העברות בעלות חתומות לרישום המקרקעין הישראלי (טאבו). ' +
          'מאמת מזהה חלקה טאבו (פורמט גוש-חלקה), זהות קונה ומוכר, ' +
          'שווי עסקה, ואישור מס רכישה. ' +
          'מייצר חבילת הגשה חתומה ללשכת הרישום — הוכחה עם חותמת זמן של ' +
          'מה הוגש ומתי שמגנה על שני הצדדים בכל סכסוך.',
        starter_template:
          'שני חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'deed-validator → tabu-registrar. ' +
          'סכסוכי רישום נפתרים עם hash אחד — ללא קלסר, ללא פקס.',
        overview: {
          pain:
            'עסקאות מקרקעין מזיזות מיליוני שקלים על מסמכים שאף אחד לא יכול לאמת באופן עצמאי — עד שמשהו משתבש. אישור מס רכישה חסר שמתגלה שלושה שבועות אחרי שכולם חשבו שהעסקה נסגרה. הגשה לטאבו בפקס בלי תיעוד למה נשלח. עיקול מתחרה שנרשם בין החוזה לרישום — והקרב על חותמת הזמן הוא המילה שלכם מול שלהם.',
          solution:
            'כל שטר מאומת בביצוע: פורמט גוש-חלקה, זהות קונה ומוכר, שווי, אישור מס רכישה. חבילת ההגשה לטאבו נחתמת בהגשה — הוכחה למה הוגש בדיוק ומתי, הרבה אחרי שלשכת הטאבו מעבדת את הבקשה.',
          how_it_works: [
            'שטר מאומת בביצוע — מזהה חלקה, צדדים, שווי, ואישור מס רכישה נחתמים באירוע אחד; פגמים קריטיים חוסמים את הצינור.',
            'חבילת ההגשה לטאבו נחתמת בהגשה — רישיון עורך דין, מספר טופס, עקביות חלקה — הכול משורשר ל-hash השטר.',
            'עיקול מתחרה או עיכוב רישום? פותחים את השרשרת, מציגים את חותמת הזמן של האישור וההגשה — הסכסוך נסגר בשיחת טלפון.',
          ],
          who_for:
            'עורכי דין מקרקעין, עוסקים בנכסים, יזמים ומשקיעים מוסדיים בישראל שמטפלים ב-5+ עסקאות בשנה — ומי שחווה עיכוב רישום בגלל תיעוד חסר או לא ניתן לאימות.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.realEstateRental as EntityId,
    slug: 'realestate-rental',
    domain: 'Process' as DomainType,
    price_usd: 3000,
    monthly_price_usd: 3900,
    contracts_count: 2,
    inputs: [{ name: 'rental_agreement', data_type: 'Document', required: true }],
    outputs: [{ name: 'deposit_certificate', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Real Estate Rental™',
        tagline: 'Every tenancy, fair and tamper-proof.',
        description:
          'Signed rental agreements and deposit protection for Israeli residential ' +
          'tenancies. Validates rental period, monthly rent, deposit cap compliance ' +
          '(≤ 3 months\' rent under Fair Rental Law §25), and deposit return ' +
          'conditions. Every deposit is signed at collection — the tenant\'s ' +
          'tamper-proof proof of payment that resolves small-claims disputes ' +
          'before they reach a judge.',
        starter_template:
          'Two signed contracts compose into one Conductor: ' +
          'rental-validator → deposit-guard. ' +
          'Deposit disputes resolve in a phone call — not a six-month court case.',
        overview: {
          pain:
            'The Fair Rental Law has been in force since 2017, but deposit and maintenance disputes still generate tens of thousands of small-claims cases a year. Almost none needed to reach court — they hinged on what the parties agreed at signing and whether the deposit was actually collected when claimed. A signed agreement is the difference between a five-minute call and a six-month case.',
          solution:
            'Every rental agreement signed at execution — parties, period, monthly rent, maintenance clause. Every deposit checked against the 3-month cap under Fair Rental Law §25 and signed at collection. The chain in mediation or court is the chain both sides actually signed; non-compliant deposits are refused before any funds change hands.',
          how_it_works: [
            'Agreement validated at signing — parties, period, rent, and maintenance clause signed; missing mandatory clauses refuse the lease before any keys change hands.',
            'Deposit checked against the 3-month cap (Fair Rental Law §25) and return conditions; non-compliant deposits refused before transfer, every collection signed.',
            'Tenant or landlord challenges a clause; the signed chain references the original — the claim usually withdraws before reaching court.',
          ],
          who_for:
            'Israeli landlords, property-management companies with 5+ residential units, and tenants who want a signed, timestamped record of every agreement and deposit transaction.',
        },
      },
      he: {
        name: 'Real Estate Rental™',
        tagline: 'כל שכירות, הוגנת ובלתי ניתנת לשינוי.',
        description:
          'חוזי שכירות חתומים והגנת פיקדון לשכירויות מגורים ישראליות. ' +
          'מאמת תקופת שכירות, שכר דירה חודשי, עמידה בתקרת פיקדון ' +
          '(≤ 3 חודשי שכר — חוק שכירות הוגנת §25), ותנאי החזרת הפיקדון. ' +
          'כל פיקדון חתום בגבייה — הוכחה בלתי ניתנת לשינוי של השוכר שפותרת ' +
          'תביעות תביעות קטנות לפני שהן מגיעות לשופט.',
        starter_template:
          'שני חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'rental-validator → deposit-guard. ' +
          'סכסוכי פיקדון נפתרים בשיחת טלפון — לא בתהליך בית משפט של חצי שנה.',
        overview: {
          pain:
            'חוק שכירות הוגנת בתוקף מאז 2017, אך סכסוכי פיקדון ותחזוקה עדיין מייצרים עשרות אלפי תיקי תביעות קטנות בשנה. כמעט אף אחד מהם לא היה צריך להגיע לבית המשפט — הם נשענו על מה שהוסכם בחתימה ואם הפיקדון באמת נגבה כפי שנטען. חוזה חתום הוא ההבדל בין שיחת חמש דקות לתיק של שישה חודשים.',
          solution:
            'כל הסכם שכירות נחתם בביצוע — צדדים, תקופה, שכר חודשי, סעיף תחזוקה. כל פיקדון נבדק מול תקרת 3 חודשי שכר (חוק שכירות הוגנת §25) ונחתם בגבייה. השרשרת בגישור או בבית משפט היא השרשרת ששני הצדדים באמת חתמו; פיקדון לא תואם נדחה לפני שכסף מחליף ידיים.',
          how_it_works: [
            'הסכם מאומת בחתימה — צדדים, תקופה, שכר וסעיף תחזוקה נחתמים; סעיפי חובה חסרים מסרבים להסכם לפני שמפתחות מחליפות ידיים.',
            'פיקדון נבדק מול תקרת 3 חודשי שכר (חוק שכירות הוגנת §25) ותנאי החזרה; פיקדון לא תואם נדחה לפני העברה, כל גבייה נחתמת.',
            'שוכר או משכיר מערער על סעיף; השרשרת החתומה מצביעה למקור — התביעה בדרך כלל נסוגה לפני בית המשפט.',
          ],
          who_for:
            'בעלי דירה, חברות ניהול נכסים עם 5+ יחידות מגורים, ושוכרים בישראל שרוצים רשומה חתומה ועם חותמת זמן לכל הסכם ועסקת פיקדון.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.codeProof as EntityId,
    slug: 'code-proof',
    domain: { Custom: 'Education' } as DomainType,
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 1,
    inputs: [{ name: 'github_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'signed_proof', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Code Proof™',
        tagline: 'Every commit, every release — signed at the moment it ships.',
        description:
          'Every push, every merged PR, every published release — captured ' +
          'in your Vault as a signed proof. Authorship dispute closed before ' +
          'it opens. Connect a GitHub PAT once; signing happens within seconds.',
        starter_template:
          'Connect your GitHub PAT, push as usual. Each event lands in the ' +
          'Vault with a signed manifest pointing at /v/<root_id>.',
      },
      he: {
        name: 'הוכחת קוד™',
        tagline: 'כל קומיט, כל שחרור — חתום ברגע השליחה.',
        description:
          'כל push, כל merge וכל release — נלכדים בכספת כהוכחה חתומה. ' +
          'מחלוקות יוצר נסגרות לפני שהן נפתחות. חיבור PAT של גיטהאב פעם אחת; ' +
          'חתימה תוך שניות.',
        starter_template:
          'מחברים PAT של גיטהאב, ממשיכים כרגיל. כל אירוע נוחת בכספת עם ' +
          'מניפסט חתום שמפנה ל-/v/<root_id>.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.ideaProof as EntityId,
    slug: 'idea-proof',
    domain: { Custom: 'Education' } as DomainType,
    price_usd: 990,
    monthly_price_usd: 1287,
    contracts_count: 1,
    inputs: [{ name: 'notion_page_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'signed_proof', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Idea Proof™',
        tagline: "Ideas, signed the moment they're written down.",
        description:
          'Every Notion page-create and page-update is captured as a signed ' +
          'event in your Vault. Content stays in Notion; only the hash and ' +
          'metadata enter the proof. The receipt that says "I had this idea ' +
          'first" becomes math instead of memory.',
        starter_template:
          'Connect a Notion Integration token, write as usual. Polling at ' +
          '60 s captures every change.',
      },
      he: {
        name: 'הוכחת רעיון™',
        tagline: 'רעיונות, חתומים ברגע שהם נכתבים.',
        description:
          'כל יצירת ועדכון של עמוד נושן נלכדים כאירוע חתום בכספת. התוכן ' +
          'נשאר בנושן; רק ה-hash והמטה-דאטה נכנסים להוכחה. הקבלה שאומרת "היה ' +
          'לי את הרעיון ראשון" הופכת ממתמטיקה במקום מזיכרון.',
        starter_template:
          'מחברים Integration Token של נושן, כותבים כרגיל. polling כל 60 ' +
          'שניות לוכד כל שינוי.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.freelanceContract as EntityId,
    slug: 'freelance-contract',
    domain: 'Financial' as DomainType,
    price_usd: 2980,
    monthly_price_usd: 3874,
    contracts_count: 1,
    inputs: [{ name: 'engagement_intake', data_type: 'Document', required: true }],
    outputs: [{ name: 'engagement_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Freelance Contract™',
        tagline: 'Email + payment, signed end-to-end. Disputes ended before they start.',
        description:
          'The SOW email, every milestone deliverable, and the matching Stripe ' +
          'payment intent — bundled into one signed envelope per engagement. ' +
          'Three independent third-party records, one verifier URL.',
        starter_template:
          'Forward the SOW email to a per-engagement intake address; hash ' +
          'each deliverable on ship; the connector links the matching Stripe ' +
          'payment intent and signs the bundle.',
      },
      he: {
        name: 'חוזה פרילנס™',
        tagline: 'מייל + תשלום, חתומים מקצה-לקצה. סוף לוויכוחים לפני שהם מתחילים.',
        description:
          'מייל ה-SOW, כל deliverable וה-Stripe payment intent התואם — ' +
          'נארזים למעטפה חתומה אחת לכל התקשרות. שלוש רשומות עצמאיות מצד שלישי, ' +
          'verifier URL אחד.',
        starter_template:
          'forward למייל ה-SOW לכתובת קליטה ייעודית להתקשרות; hash לכל ' +
          'deliverable במסירה; הקונקטור מקשר את ה-Stripe payment intent וחותם ' +
          'על ה-bundle.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.beautyProtocol as EntityId,
    slug: 'beauty-protocol',
    domain: 'Generic' as DomainType,
    price_usd: 990,
    monthly_price_usd: 1287,
    contracts_count: 1,
    inputs: [{ name: 'creator_post_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'signed_proof', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Beauty Protocol™',
        tagline: 'Your routine. Signed. Anti-copycat from the moment you post.',
        description:
          'Every TikTok video and Instagram media is signed within 60 s of ' +
          'publishing. Caption hash, thumbnail SHA-256, share URL, engagement ' +
          'stats. Earlier signed timestamp wins every authorship dispute.',
        starter_template:
          'OAuth into TikTok and Meta with read-only scopes. Post as usual.',
      },
      he: {
        name: 'פרוטוקול יופי™',
        tagline: 'השגרה שלך. חתומה. הגנה מפני העתקות מרגע הפרסום.',
        description:
          'כל סרטון בטיקטוק וכל מדיה באינסטגרם נחתמים תוך 60 שניות מהפרסום. ' +
          'caption hash, SHA-256 של thumbnail, share URL, engagement stats. ' +
          'חותמת זמן חתומה מוקדמת מנצחת בכל מחלוקת על יוצר.',
        starter_template:
          'OAuth לטיקטוק ול-Meta עם read-only. ממשיכים לפרסם כרגיל.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.fitnessPlanProof as EntityId,
    slug: 'fitness-plan-proof',
    domain: 'Generic' as DomainType,
    price_usd: 990,
    monthly_price_usd: 1287,
    contracts_count: 1,
    inputs: [{ name: 'tiktok_video_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'signed_proof', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Fitness Plan Proof™',
        tagline: 'Workout videos, signed at upload. Your plans stay yours.',
        description:
          'Every published TikTok fitness video is signed at upload — caption ' +
          'hash, thumbnail hash, exercise count, equipment list, duration, ' +
          'engagement stats. Original program creators win attribution.',
        starter_template:
          'OAuth into TikTok with read-only scopes (video.list, video.insights).',
      },
      he: {
        name: 'הוכחת תוכנית כושר™',
        tagline: 'סרטוני אימון, חתומים בהעלאה. התוכניות נשארות שלך.',
        description:
          'כל סרטון אימון שפורסם בטיקטוק נחתם בהעלאה — caption hash, ' +
          'thumbnail hash, מספר תרגילים, רשימת ציוד, משך, engagement stats. ' +
          'יוצרי תוכניות מקוריות מנצחים בייחוס.',
        starter_template:
          'OAuth לטיקטוק עם הרשאות read-only (video.list, video.insights).',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.recipeCreator as EntityId,
    slug: 'recipe-creator',
    domain: 'Generic' as DomainType,
    price_usd: 990,
    monthly_price_usd: 1287,
    contracts_count: 1,
    inputs: [{ name: 'instagram_media_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'signed_proof', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Recipe Creator™',
        tagline: 'Every recipe, signed. Original creator wins every dispute.',
        description:
          'Every Instagram reel, carousel, and image at publish time. Media ' +
          'metadata + caption hash + thumbnail hash + structured ingredient ' +
          'list. Recipe identity = ingredient set + method, signed by the ' +
          'original creator.',
        starter_template:
          'OAuth into Meta via Facebook Login. IG account must be Business ' +
          'or Creator. Read-only.',
      },
      he: {
        name: 'יוצר מתכונים™',
        tagline: 'כל מתכון, חתום. היוצר המקורי מנצח בכל מחלוקת.',
        description:
          'כל reel, carousel ותמונה באינסטגרם בזמן הפרסום. מטה-דאטה של מדיה + ' +
          'caption hash + thumbnail hash + רשימת מרכיבים מובנית. זהות המתכון = ' +
          'סט מרכיבים + שיטה, חתומים ע"י היוצר המקורי.',
        starter_template:
          'OAuth ל-Meta דרך Facebook Login. חשבון IG חייב להיות Business או ' +
          'Creator. read-only.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.healthDiary as EntityId,
    slug: 'health-diary',
    domain: 'Medical' as DomainType,
    price_usd: 1480,
    monthly_price_usd: 1924,
    contracts_count: 1,
    inputs: [{ name: 'diary_entry', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_chain_entry', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Health Diary™',
        tagline: 'Symptoms, supplements, sleep — signed at the moment they happen.',
        description:
          'Every form submission — symptoms, supplements, sleep, vitals, mood, ' +
          'meals — signed within seconds with the patient\'s Ed25519 key + ' +
          'RFC 3161 timestamp + a hash chain to the previous entry. The ' +
          'chronology a clinician trusts mathematically.',
        starter_template:
          'Drop the form into your tracker app or daily routine; entries land ' +
          'in the Vault as a tamper-evident chronological chain.',
      },
      he: {
        name: 'יומן בריאות™',
        tagline: 'תסמינים, תוספים, שינה — חתומים ברגע שהם קורים.',
        description:
          'כל form submission — תסמינים, תוספים, שינה, מדדים, מצב רוח, ארוחות — ' +
          'נחתם תוך שניות במפתח Ed25519 של המטופל + חותמת RFC 3161 + שרשרת ' +
          'hash לרשומה הקודמת. כרונולוגיה שרופא סומך עליה מתמטית.',
        starter_template:
          'משלבים את ה-form באפליקציית tracker או בשגרה היומית; הרשומות נוחתות ' +
          'בכספת כשרשרת כרונולוגית tamper-evident.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.videoFromScript as EntityId,
    slug: 'video-from-script',
    domain: { Custom: 'creative' } as DomainType,
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 3,
    inputs: [{ name: 'topic_or_script', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_video_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Video From Script™',
        tagline: 'Every frame, signed at source.',
        description:
          'End-to-end signed video production. The Conductor chains OpenAI for the screenplay, ' +
          'ElevenLabs for the voiceover, and Runway for the final render. Every stage emits an ' +
          'Ed25519-signed attestation; the bundle ships with the signed script, signed audio, ' +
          'and signed video so creators, agencies, and platforms can prove who authored every frame.',
        starter_template:
          'Three signed contracts compose into one Conductor: ' +
          'script-generator → voice-synthesizer → video-renderer. ' +
          'Single verifier URL; chain verifies offline.',
      },
      he: {
        name: 'Video From Script™',
        tagline: 'כל פריים, חתום במקור.',
        description:
          'הפקת וידאו חתומה מקצה לקצה. ה-Conductor מחבר את OpenAI לסקריפט, את ElevenLabs ' +
          'לקריינות, ואת Runway לרנדור הסופי. כל שלב מנפיק תעודת Ed25519 חתומה; החבילה ' +
          'מגיעה עם סקריפט חתום, אודיו חתום ווידאו חתום — כך שיוצרים, סוכנויות ופלטפורמות ' +
          'יכולים להוכיח מי יצר כל פריים.',
        starter_template:
          'שלושה חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'script-generator → voice-synthesizer → video-renderer. ' +
          'verifier URL אחד; השרשרת מתאמתת לא־מקוון.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.promoVideo as EntityId,
    slug: 'promo-video',
    domain: { Custom: 'creative' } as DomainType,
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 4,
    inputs: [{ name: 'product_brief', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_promo_video', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Promo Video™',
        tagline: '30 seconds, signed at the source.',
        description:
          '30-second product promos, signed end-to-end. The Conductor chains OpenAI for the ' +
          'promo script, ElevenLabs for the voiceover, Stability AI for the visuals, and Runway ' +
          'for assembly. Every stage emits an Ed25519-signed attestation; the bundle proves the ' +
          'finished promo was generated from this product, this tagline, and these features.',
        starter_template:
          'Four signed contracts compose into one Conductor: ' +
          'promo-script → voice → visuals → assembly. ' +
          '1080p deliverable, audio multiplexed, single verifier URL.',
      },
      he: {
        name: 'Promo Video™',
        tagline: '30 שניות, חתומות במקור.',
        description:
          'פרומואי מוצר של 30 שניות, חתומים מקצה לקצה. ה-Conductor מחבר את OpenAI לסקריפט ' +
          'הפרומו, את ElevenLabs לקריינות, את Stability AI לוויזואלים, ואת Runway להרכבה. כל ' +
          'שלב מנפיק תעודת Ed25519 חתומה; החבילה מוכיחה שהפרומו המוגמר יוצר מהמוצר, מהטאגליין, ' +
          'ומהפיצ׳רים האלה.',
        starter_template:
          'ארבעה חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'promo-script → voice → visuals → assembly. ' +
          'מסירה 1080p, אודיו מוטבע, verifier URL אחד.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.explainerVideo as EntityId,
    slug: 'explainer-video',
    domain: { Custom: 'creative' } as DomainType,
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 4,
    inputs: [{ name: 'topic_brief', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_explainer_video', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Explainer Video™',
        tagline: 'Every concept, signed and sourced.',
        description:
          'Signed explainer videos at three durations (60 / 90 / 120 s). The Conductor chains ' +
          'OpenAI for the script, ElevenLabs for narration, Stability AI for illustrations, and ' +
          'Runway for animation. Compliance teams + LMS admins resolve a single hash and walk ' +
          'the chain from topic to every frame.',
        starter_template:
          'Four signed contracts compose into one Conductor: ' +
          'explainer-script → narration → illustrations → animation. ' +
          'Three preset lengths; single verifier URL.',
      },
      he: {
        name: 'Explainer Video™',
        tagline: 'כל מושג, חתום וממוקור.',
        description:
          'סרטוני explainer חתומים בשלושה אורכים (60 / 90 / 120 שניות). ה-Conductor מחבר ' +
          'את OpenAI לסקריפט, את ElevenLabs לקריינות, את Stability AI לאיורים, ואת Runway ' +
          'לאנימציה. צוותי ציות ומנהלי LMS פותחים hash אחד וצועדים מהנושא לכל פריים.',
        starter_template:
          'ארבעה חוזים חתומים מורכבים ב-Conductor אחד: ' +
          'explainer-script → narration → illustrations → animation. ' +
          'שלושה אורכים מוגדרים; verifier URL אחד.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.signedVoiceover as EntityId,
    slug: 'signed-voiceover',
    domain: { Custom: 'creative' } as DomainType,
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 1,
    inputs: [{ name: 'voice_brief', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_voiceover_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Signed Voiceover™',
        tagline: 'The voice you used. Provably.',
        description:
          'Single-stage signed voiceover production. ElevenLabs synthesises the audio in any of ' +
          '29 languages; the engine emits an Ed25519-signed attestation that binds the audio ' +
          'bytes, transcript, voice id, and timestamp into one verifiable record. Survives ' +
          're-encode and deepfake claims alike.',
        starter_template:
          'Single signed contract: voice-synthesizer. ' +
          'Output bundle = signed MP3 + signed transcript JSON, ' +
          'one hash, ElevenLabs Multilingual v2.',
      },
      he: {
        name: 'Signed Voiceover™',
        tagline: 'הקול שהשתמשתם בו. מוכח.',
        description:
          'הפקת voiceover חתומה חד-שלב. ElevenLabs מפיק את האודיו בכל אחת מ-29 השפות; ' +
          'המנוע מנפיק תעודת Ed25519 חתומה שקושרת את ה-bytes של האודיו, התמליל, voice id ' +
          'וחותמת הזמן לרשומה אחת ניתנת לאימות. שורד re-encode וטענות deepfake כאחד.',
        starter_template:
          'חוזה חתום אחד: voice-synthesizer. ' +
          'חבילת פלט = MP3 חתום + transcript JSON חתום, ' +
          'hash אחד, ElevenLabs Multilingual v2.',
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.thumbnailGenerator as EntityId,
    slug: 'thumbnail-generator',
    domain: { Custom: 'creative' } as DomainType,
    price_usd: 1980,
    monthly_price_usd: 2574,
    contracts_count: 1,
    inputs: [{ name: 'thumbnail_brief', data_type: 'Document', required: true }],
    outputs: [{ name: 'signed_thumbnail', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Thumbnail Generator™',
        tagline: 'The image that won the click. Provably yours.',
        description:
          'Single-stage signed thumbnail production. Stability AI generates the image from ' +
          'caller title, style, and dimensions; the engine signs the image bytes + render ' +
          'parameters + timestamp. Five style presets and five resolution presets cover YouTube, ' +
          'Instagram, OpenGraph, and vertical-story formats.',
        starter_template:
          'Single signed contract: thumbnail-renderer. ' +
          'PNG deliverable; Stability AI SDXL; ' +
          'verifier URL drops into post copy.',
      },
      he: {
        name: 'Thumbnail Generator™',
        tagline: 'התמונה שזכתה בלחיצה. שלך, מוכח.',
        description:
          'הפקת thumbnail חתומה חד-שלב. Stability AI מייצר את התמונה מכותרת, סגנון ומימדים; ' +
          'המנוע חותם על PNG bytes + פרמטרי הרנדור + חותמת זמן. חמישה פריסטי סגנון וחמישה ' +
          'פריסטי רזולוציה מכסים YouTube, Instagram, OpenGraph ופורמטים אנכיים של story.',
        starter_template:
          'חוזה חתום אחד: thumbnail-renderer. ' +
          'מסירת PNG; Stability AI SDXL; ' +
          'verifier URL נכנס לתוכן הפוסט.',
      },
    },
  },
  // ── Capital markets recipes (R01–R12) ──────────────────────────────────
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmTradeReconstruction as EntityId,
    slug: 'cm-trade-reconstruction',
    domain: 'Financial' as DomainType,
    price_usd: 15000,
    monthly_price_usd: 19500,
    contracts_count: 2,
    inputs: [{ name: 'trade_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'attestation_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Trade Reconstruction™',
        tagline: 'Complete MiFIR / CAT audit trail — signed at every lifecycle event.',
        description:
          'Signs every lifecycle event — order, routing, fill, allocation, ' +
          'confirmation, settlement — at the moment it occurs. Satisfies ' +
          'MiFIR Art. 25, SEC CAT, and CFTC Part 45 in a single conductor run.',
        starter_template:
          'Two signed contracts: trade-reconstructor → execution-validator. ' +
          'Chain your OMS/EMS events once; hand any regulator the bundle in seconds.',
        overview: {
          pain:
            'Regulators demand a complete microsecond-timestamped audit trail for every ' +
            'trade lifecycle event. Assembling it from five siloed systems after a subpoena ' +
            'arrives takes weeks and produces gaps that trigger enforcement actions.',
          solution:
            'Trade Reconstruction™ signs every lifecycle event — order, routing, fill, ' +
            'allocation, confirmation, settlement — at the moment it occurs. One conductor ' +
            'run produces a tamper-evident bundle that satisfies MiFIR Art. 25, SEC CAT, ' +
            'and CFTC Part 45 simultaneously.',
          how_it_works: [
            'Connect your OMS/EMS trade event stream',
            'Engine signs each event with Ed25519 + RFC 3161 timestamp on arrival',
            'Events are hash-chained into a tamper-evident lifecycle bundle',
            'Export the signed bundle to any regulator in seconds',
          ],
          who_for:
            'Head of Compliance and Chief Compliance Officers at broker-dealers, ' +
            'investment banks, and multi-asset trading firms operating under MiFIR, ' +
            'SEC CAT, or CFTC Part 45 reporting obligations.',
        },
      },
      he: {
        name: 'שחזור עסקת מסחר™',
        tagline: 'שרשרת ביקורת MiFIR/CAT מלאה — חתומה בכל אירוע מחזור חיים.',
        description:
          'חותם כל אירוע מחזור חיים — הוראה, ניתוב, ביצוע, הקצאה, אישור, סליקה — ' +
          'ברגע שהוא מתרחש. עמידה ב-MiFIR Art. 25, SEC CAT ו-CFTC Part 45 בהרצה אחת.',
        starter_template:
          'שני חוזים חתומים: trade-reconstructor → execution-validator. ' +
          'חיבור OMS/EMS פעם אחת; הגשה לרגולטור תוך שניות.',
        overview: {
          pain:
            'הרגולטורים דורשים שרשרת ביקורת מלאה ומוחתמת-זמן לכל אירוע מחזור חיים. ' +
            'איסופה ממערכות מבודדות לאחר קבלת זימון לחקירה לוקח שבועות ומייצר פערים.',
          solution:
            'שחזור עסקת מסחר™ חותם כל אירוע ברגע שהוא מתרחש. הרצה אחת מייצרת חבילה ' +
            'חסינת מניפולציה שעומדת ב-MiFIR Art. 25, SEC CAT ו-CFTC Part 45 במקביל.',
          how_it_works: [
            'חיבור זרם אירועי OMS/EMS',
            'המנוע חותם כל אירוע ב-Ed25519 + חותמת RFC 3161 בקליטה',
            'אירועים משורשרים בהאש לחבילת מחזור חיים חסינת מניפולציה',
            'ייצוא החבילה החתומה לכל רגולטור תוך שניות',
          ],
          who_for:
            'ראש ציות ומנהלי ציות ראשיים בברוקרים, בנקי השקעות ובתי מסחר רב-נכסיים ' +
            'הפועלים תחת חובות דיווח MiFIR, SEC CAT או CFTC Part 45.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmBestExecutionAttestation as EntityId,
    slug: 'cm-best-execution-attestation',
    domain: 'Financial' as DomainType,
    price_usd: 12000,
    monthly_price_usd: 15600,
    contracts_count: 1,
    inputs: [{ name: 'order_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'best_ex_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Best Execution Attestation™',
        tagline: 'Per-order signed proof of best execution against MiFID II Art. 27.',
        description:
          'Signs the pre-trade EBBO, venue selection decision, and post-trade ' +
          'price comparison per order. Produces a tamper-evident RTS 27-compatible ' +
          'attestation — not a monthly aggregate, a per-order proof.',
        starter_template:
          'Single signed contract: best-execution-checker. ' +
          'Feed each order event; get an RTS 27 attestation per order.',
        overview: {
          pain:
            'MiFID II Art. 27 requires firms to prove best execution per order, ' +
            'but most systems capture only aggregated monthly statistics. When a ' +
            'regulator or client demands order-level evidence, there is nothing to show.',
          solution:
            'Best Execution Attestation™ signs the pre-trade EBBO, venue selection ' +
            'rationale, and post-trade price comparison at the moment each order is ' +
            'handled — producing an RTS 27-compatible, per-order tamper-evident proof.',
          how_it_works: [
            'Feed each order event into the conductor at routing time',
            'Engine captures EBBO snapshot, venue selection reason, and fill price',
            'Ed25519 signature + RFC 3161 timestamp locks the record in place',
            'Attestation delivered per-order; aggregate reports auto-generated monthly',
          ],
          who_for:
            'Heads of Best Execution, MiFID II compliance officers, and trading ' +
            'operations teams at investment firms, systematic internalizers, and ' +
            'agency brokers handling EU equity and bond orders.',
        },
      },
      he: {
        name: 'אישור ביצוע מיטבי™',
        tagline: 'הוכחה חתומה לכל הוראה של ביצוע מיטבי מול MiFID II Art. 27.',
        description:
          'חותם EBBO לפני עסקה, בחירת בורסה והשוואת מחיר אחרי עסקה לכל הוראה. ' +
          'מייצר אישור תואם RTS 27 חסין מניפולציה — לא סיכום חודשי, הוכחה לפי-הוראה.',
        starter_template:
          'חוזה חתום אחד: best-execution-checker. ' +
          'הזנת כל אירוע הוראה; קבלת אישור RTS 27 לכל הוראה.',
        overview: {
          pain:
            'MiFID II Art. 27 מחייב חברות להוכיח ביצוע מיטבי לכל הוראה, אך רוב המערכות ' +
            'לוכדות רק סטטיסטיקות חודשיות מצטברות. כשרגולטור או לקוח דורש ראיות ברמת הוראה — אין מה להציג.',
          solution:
            'אישור ביצוע מיטבי™ חותם EBBO, נימוק בחירת בורסה והשוואת מחיר לאחר ביצוע ' +
            'ברגע הטיפול בכל הוראה — ומייצר הוכחה חסינת מניפולציה תואמת RTS 27 לפי הוראה.',
          how_it_works: [
            'הזנת כל אירוע הוראה לקונדקטור בעת הניתוב',
            'המנוע לוכד תמונת EBBO, נימוק בחירת בורסה ומחיר ביצוע',
            'חתימת Ed25519 + חותמת RFC 3161 נועלות את הרשומה',
            'אישור נמסר לפי הוראה; דוחות צבירה חודשיים נוצרים אוטומטית',
          ],
          who_for:
            'ראשי ביצוע מיטבי, קציני ציות MiFID II וצוותי תפעול מסחר בחברות השקעה, ' +
            'מפנימי שיטתיים וברוקרים סוכנות המטפלים בהוראות מניות ואג"ח אירופיות.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmMifirEmirCatTriReport as EntityId,
    slug: 'cm-mifir-emir-cat-tri-report',
    domain: 'Financial' as DomainType,
    price_usd: 20000,
    monthly_price_usd: 26000,
    contracts_count: 2,
    inputs: [{ name: 'trade_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'regulatory_reports', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Multi-Regime Transaction Report™',
        tagline: 'One trade in — MiFIR, EMIR, CAT, and TRI out.',
        description:
          'Single-input fan-out to four regulatory reporting regimes in one ' +
          'conductor run. All four reports carry the same originating trade hash — ' +
          'any regulator can verify they describe the same event.',
        starter_template:
          'Two signed contracts: mifir-formatter → emir-formatter. ' +
          'Route your OMS trade feed once; all four regimes covered.',
        overview: {
          pain:
            'Global trading desks face four overlapping reporting regimes — MiFIR, ' +
            'EMIR, SEC CAT, and TRI — each with different field mappings, deadlines, ' +
            'and submission portals. Maintaining four separate pipelines multiplies ' +
            'cost, latency, and reconciliation failures.',
          solution:
            'Multi-Regime Transaction Report™ fans a single OMS trade event out to ' +
            'all four regime-specific report formats in one conductor run. Every report ' +
            'carries the originating trade hash, so any regulator can verify all four ' +
            'describe the same event.',
          how_it_works: [
            'Route your OMS trade feed into the single conductor input',
            'Engine normalizes the event and maps fields to MiFIR, EMIR, CAT, and TRI schemas',
            'All four signed reports share the same originating trade hash',
            "Deliver to each regulator's submission portal; one record, four regimes",
          ],
          who_for:
            'Regulatory reporting teams and COOs at global broker-dealers, prime ' +
            'brokers, and trading firms reporting across EU, US, and international ' +
            'regimes simultaneously.',
        },
      },
      he: {
        name: 'דוח עסקה רב-משטרי™',
        tagline: 'עסקה אחת פנימה — MiFIR, EMIR, CAT ו-TRI החוצה.',
        description:
          'פאן-אאוט לארבעה משטרי דיווח רגולטורי בהרצת קונדקטור אחת. ' +
          'כל ארבעת הדוחות נושאים את אותו hash מקור — כל רגולטור יכול לאמת.',
        starter_template:
          'שני חוזים חתומים: mifir-formatter → emir-formatter. ' +
          'ניתוב OMS פעם אחת; ארבעה משטרים מכוסים.',
        overview: {
          pain:
            'שולחנות מסחר גלובליים עומדים בפני ארבעה משטרי דיווח חופפים — MiFIR, EMIR, SEC CAT ו-TRI — ' +
            'כל אחד עם מיפוי שדות, מועדים ופורטלי הגשה שונים. תחזוקת ארבעה צינורות נפרדים מכפילה עלויות.',
          solution:
            'דוח עסקה רב-משטרי™ מפן אירוע עסקה אחד מה-OMS לארבעה פורמטים ממשטרים שונים בהרצה אחת. ' +
            'כל דוח נושא את hash העסקה המקורי לאימות צלבי.',
          how_it_works: [
            'ניתוב זרם עסקאות OMS לקלט הקונדקטור היחיד',
            'המנוע ממפה שדות לסכמות MiFIR, EMIR, CAT ו-TRI',
            'כל ארבעת הדוחות החתומים חולקים hash עסקה מקורי זהה',
            'הגשה לפורטל כל רגולטור; רשומה אחת, ארבעה משטרים',
          ],
          who_for:
            'צוותי דיווח רגולטורי ומנהלי תפעול בברוקרים גלובליים ובתי מסחר המדווחים ' +
            'במקביל למשטרים אירופיים, אמריקאים ובינלאומיים.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmNavAttestation as EntityId,
    slug: 'cm-nav-attestation',
    domain: 'Financial' as DomainType,
    price_usd: 18000,
    monthly_price_usd: 23400,
    contracts_count: 2,
    inputs: [{ name: 'pricing_feed', data_type: 'Stream', required: true }],
    outputs: [{ name: 'nav_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'NAV Attestation™',
        tagline: 'Daily NAV — signed from pricing source to published figure.',
        description:
          'Signs each pricing source at ingestion, re-derives the NAV, and ' +
          'compares against the administrator\'s figure within a configured tolerance. ' +
          'Satisfies UCITS Art. 85, AIFMD Art. 19, and SEC Rule 2a-4.',
        starter_template:
          'Two signed contracts: pricing-source-auditor → nav-calculator-validator. ' +
          'Connect your pricing feeds; depository gets a signed bundle daily.',
        overview: {
          pain:
            'Fund administrators publish a daily NAV figure but regulators and ' +
            'depositories cannot independently verify the calculation path — from ' +
            'each pricing source through to the final published number. Errors or ' +
            'manipulation go undetected until an audit months later.',
          solution:
            'NAV Attestation™ signs each pricing source at ingestion, independently ' +
            're-derives the NAV, and flags any deviation from the administrator\'s ' +
            'figure beyond configured tolerance — producing a daily tamper-evident ' +
            'bundle that satisfies UCITS Art. 85, AIFMD Art. 19, and SEC Rule 2a-4.',
          how_it_works: [
            'Connect Bloomberg, Reuters, or proprietary pricing feeds',
            'Engine signs each price at ingestion with timestamp and source identity',
            'NAV is independently re-derived and compared against admin figure',
            'Daily signed bundle delivered to depository and CCO dashboard',
          ],
          who_for:
            'Fund CFOs, depositories, and compliance officers at UCITS funds, ' +
            'alternative investment funds, and US registered investment companies ' +
            'subject to daily NAV calculation requirements.',
        },
      },
      he: {
        name: 'אישור שווי נכסי נקי™',
        tagline: 'NAV יומי — חתום ממקור תמחור ועד נתון מפורסם.',
        description:
          'חותם כל מקור תמחור בקליטה, מגזיר מחדש NAV ומשווה לנתון מנהל בתוך סבילות. ' +
          'עמידה ב-UCITS Art. 85, AIFMD Art. 19 ו-SEC Rule 2a-4.',
        starter_template:
          'שני חוזים חתומים: pricing-source-auditor → nav-calculator-validator. ' +
          'חיבור מקורות תמחור; בנק משמורת מקבל חבילה חתומה יומית.',
        overview: {
          pain:
            'מנהלי קרנות מפרסמים NAV יומי אך רגולטורים ובנקי משמורת אינם יכולים לאמת ' +
            'את נתיב החישוב ממקור התמחור ועד הנתון המפורסם. שגיאות ומניפולציות נחשפות רק בביקורת.',
          solution:
            'אישור NAV™ חותם כל מקור תמחור בקליטה, מגזיר NAV באופן עצמאי ומדגל כל סטייה ' +
            'מעבר לסבילות המוגדרת — ומייצר חבילה יומית חסינת מניפולציה תואמת UCITS, AIFMD ו-SEC.',
          how_it_works: [
            'חיבור Bloomberg, Reuters או מקורות תמחור קנייניים',
            'המנוע חותם כל מחיר בקליטה עם חותמת זמן וזהות מקור',
            'NAV מגוזר באופן עצמאי ומושווה לנתון המנהל',
            'חבילה חתומה יומית נמסרת לבנק המשמורת ולוח CCO',
          ],
          who_for:
            'מנהלי כספים, בנקי משמורת וקציני ציות בקרנות UCITS, קרנות השקעה חלופיות ' +
            'וחברות השקעה רשומות בארה"ב הכפופות לדרישות חישוב NAV יומי.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmModelLineageSnapshot as EntityId,
    slug: 'cm-model-lineage-snapshot',
    domain: 'Financial' as DomainType,
    price_usd: 25000,
    monthly_price_usd: 32500,
    contracts_count: 2,
    inputs: [{ name: 'model_artefact', data_type: 'Document', required: true }],
    outputs: [{ name: 'model_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Model Lineage Snapshot™',
        tagline: 'Every model version — signed and immutable for SR 11-7 and FRTB.',
        description:
          'Freezes a tamper-evident snapshot of any risk or pricing model: ' +
          'code hash, parameter hash, back-test results, and governance approval. ' +
          'One run satisfies Fed SR 11-7, EBA model risk guidelines, and FRTB IMA.',
        starter_template:
          'Two signed contracts: model-lineage-recorder → validation-sign-off. ' +
          'Trigger on every production model promotion.',
        overview: {
          pain:
            'Regulators and auditors require firms to prove exactly which model ' +
            'version produced a risk number or pricing output on a given date. ' +
            'Without cryptographic lineage, model versions are reconstructed from ' +
            'git logs and spreadsheets — an approach that fails under examination.',
          solution:
            'Model Lineage Snapshot™ freezes a tamper-evident snapshot at every ' +
            'production model promotion: code hash, parameter hash, back-test results, ' +
            'and governance approval — all Ed25519-signed. One run satisfies Fed SR 11-7, ' +
            'EBA model risk guidelines, and FRTB IMA simultaneously.',
          how_it_works: [
            'Trigger the conductor on each model promotion to production',
            'Engine hashes code artifacts, parameters, and back-test result sets',
            'Governance approval is captured and co-signed by the validation team',
            'Immutable snapshot stored; any later audit retrieves the exact model state',
          ],
          who_for:
            'Model Risk Management teams, Heads of Quantitative Research, and ' +
            'CROs at banks, asset managers, and trading firms subject to Fed SR 11-7, ' +
            'EBA guidelines, or FRTB Internal Models Approach requirements.',
        },
      },
      he: {
        name: 'תמונת מצב מוצא מודל™',
        tagline: 'כל גרסת מודל — חתומה ובלתי ניתנת לשינוי ל-SR 11-7 ו-FRTB.',
        description:
          'מקפיא תמונת מצב חסינת מניפולציה של כל מודל סיכון או תמחור: ' +
          'hash קוד, hash פרמטרים, תוצאות back-test ואישור ממשלי. ' +
          'עמידה ב-SR 11-7, EBA ו-FRTB IMA בהרצה אחת.',
        starter_template:
          'שני חוזים חתומים: model-lineage-recorder → validation-sign-off. ' +
          'הפעלה בכל קידום מודל לייצור.',
        overview: {
          pain:
            'רגולטורים ומבקרים דורשים הוכחה איזו גרסת מודל הפיקה מספר סיכון בתאריך נתון. ' +
            'ללא מקור קריפטוגרפי, גרסאות מודל משוחזרות מלוגים ורשימות — גישה שנכשלת בבדיקה.',
          solution:
            'תמונת מצב מוצא מודל™ מקפיאה תמונת מצב חסינת מניפולציה בכל קידום מודל לייצור: ' +
            'hash קוד, hash פרמטרים, תוצאות back-test ואישור ממשל — הכל חתום ב-Ed25519.',
          how_it_works: [
            'הפעלת הקונדקטור בכל קידום מודל לייצור',
            'המנוע מחשב hash לארטיפקטים, פרמטרים וסטים של תוצאות back-test',
            'אישור ממשלי נלכד וחתום-משותף על ידי צוות האימות',
            'תמונת מצב בלתי ניתנת לשינוי נשמרת; כל ביקורת עתידית מאחזרת את מצב המודל המדויק',
          ],
          who_for:
            'צוותי ניהול סיכוני מודל, ראשי מחקר כמותי ומנהלי סיכון ראשיים בבנקים, ' +
            'מנהלי נכסים ובתי מסחר הכפופים לדרישות SR 11-7, EBA או FRTB IMA.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmCommunicationEvidenceBundle as EntityId,
    slug: 'cm-communication-evidence-bundle',
    domain: 'Financial' as DomainType,
    price_usd: 10000,
    monthly_price_usd: 13000,
    contracts_count: 1,
    inputs: [{ name: 'communication', data_type: 'Document', required: true }],
    outputs: [{ name: 'archive_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Communication Evidence Bundle™',
        tagline: 'WORM-equivalent signed archive for MiFID II and FINRA retention.',
        description:
          'Signs every trade-related communication — Bloomberg chat, email, voice ' +
          'transcript — at ingestion. Each item receives an Ed25519 signature and ' +
          'RFC 3161 timestamp. Satisfies MiFID II Art. 16(7) and FINRA Rule 4511.',
        starter_template:
          'Single signed contract: communication-archiver. ' +
          'Route your communication platform feeds; subpoena responses in minutes.',
        overview: {
          pain:
            'MiFID II and FINRA require firms to retain trade-related communications ' +
            'in a tamper-proof format for five to seven years. Most archiving solutions ' +
            'are mutable databases — a subpoena or audit reveals the records cannot ' +
            'prove they were unchanged since capture.',
          solution:
            'Communication Evidence Bundle™ signs every Bloomberg chat, email, and ' +
            'voice transcript at the moment of ingestion with an Ed25519 signature and ' +
            'RFC 3161 timestamp — creating a WORM-equivalent cryptographic archive ' +
            'that satisfies MiFID II Art. 16(7) and FINRA Rule 4511.',
          how_it_works: [
            'Route Symphony, Bloomberg Chat, email, or voice transcripts to the connector',
            'Each item is signed with Ed25519 and RFC 3161 timestamp at ingestion',
            'Records are hash-chained to form a tamper-evident retention log',
            'Subpoena response: export the signed bundle in minutes, not weeks',
          ],
          who_for:
            'Chief Compliance Officers, e-comms surveillance teams, and legal ' +
            'counsel at broker-dealers, investment banks, and asset managers ' +
            'retaining communications under MiFID II, FINRA, or SEC 17a-4 rules.',
        },
      },
      he: {
        name: 'חבילת ראיות תקשורת™',
        tagline: 'ארכיב חתום שוות ערך WORM ל-MiFID II ו-FINRA.',
        description:
          'חותם כל תקשורת הקשורה למסחר — Bloomberg Chat, מייל, תמלול קולי — בקליטה. ' +
          'כל פריט מקבל חתימת Ed25519 וחותמת זמן RFC 3161. ' +
          'עמידה ב-MiFID II Art. 16(7) ו-FINRA Rule 4511.',
        starter_template:
          'חוזה חתום אחד: communication-archiver. ' +
          'ניתוב מקורות תקשורת; תגובות לצווים תוך דקות.',
        overview: {
          pain:
            'MiFID II ו-FINRA מחייבים חברות לשמור תקשורות הקשורות למסחר בפורמט חסין מניפולציה ' +
            'לחמש עד שבע שנים. רוב פתרונות הארכיון הם מסדי נתונים ניתנים לשינוי — ובביקורת מתגלה ' +
            'שהרשומות אינן יכולות להוכיח שלא שונו.',
          solution:
            'חבילת ראיות תקשורת™ חותמת כל Bloomberg Chat, מייל ותמלול קולי ברגע הקליטה ' +
            'עם חתימת Ed25519 וחותמת RFC 3161 — ויוצרת ארכיב קריפטוגרפי שוות ערך WORM.',
          how_it_works: [
            'ניתוב Symphony, Bloomberg Chat, מייל או תמלולי קול לקונקטור',
            'כל פריט נחתם ב-Ed25519 + חותמת RFC 3161 בקליטה',
            'רשומות משורשרות בהאש ליצירת לוג שמירה חסין מניפולציה',
            'תגובה לצו: ייצוא החבילה החתומה תוך דקות, לא שבועות',
          ],
          who_for:
            'מנהלי ציות ראשיים, צוותי מעקב תקשורות אלקטרונית ויועצים משפטיים בברוקרים, ' +
            'בנקי השקעות ומנהלי נכסים השומרים תקשורות לפי MiFID II, FINRA או SEC 17a-4.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmSurveillanceAlertEvidence as EntityId,
    slug: 'cm-surveillance-alert-with-evidence',
    domain: 'Financial' as DomainType,
    price_usd: 22000,
    monthly_price_usd: 28600,
    contracts_count: 2,
    inputs: [{ name: 'surveillance_alert', data_type: 'Stream', required: true }],
    outputs: [{ name: 'sar_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Surveillance Alert Evidence Pack™',
        tagline: 'Instant signed SAR bundle when a market surveillance alert fires.',
        description:
          'When an alert fires, packages the order-book snapshot, trade series, ' +
          'and communications into a signed SAR-ready record with unbroken chain ' +
          'of custody. Satisfies EU MAR Art. 16 and FINRA Rule 3110.',
        starter_template:
          'Two signed contracts: surveillance-alert-packager → sar-bundler. ' +
          'Wire your surveillance system; SAR bundle ready on alert.',
        overview: {
          pain:
            'When a market surveillance alert fires, compliance teams must quickly ' +
            'assemble order-book data, trade series, and communications into a ' +
            'SAR-ready package — but by the time the manual assembly is done, ' +
            'chain of custody is broken and the evidence is challenged in court.',
          solution:
            'Surveillance Alert Evidence Pack™ fires automatically when an alert ' +
            'triggers, packaging the order-book snapshot, correlated trade series, ' +
            'and captured communications into a single signed SAR bundle with an ' +
            'unbroken cryptographic chain of custody — in seconds, not days.',
          how_it_works: [
            'Wire your surveillance platform to trigger the conductor on alert',
            'Engine captures order-book snapshot, trade series, and communications at the alert moment',
            'All evidence is signed with Ed25519 and RFC 3161 timestamps',
            'SAR-ready bundle with unbroken chain of custody ready for regulators',
          ],
          who_for:
            'Market surveillance leads, compliance counsel, and MLRO officers at ' +
            'broker-dealers, trading venues, and investment banks subject to EU MAR ' +
            'Art. 16 and FINRA Rule 3110 suspicious activity reporting obligations.',
        },
      },
      he: {
        name: 'חבילת ראיות התראת מעקב™',
        tagline: 'חבילת SAR חתומה מיידית עם הפעלת התראת מעקב שוק.',
        description:
          'עם הפעלת התראה, אורזת תמונת ספר פקודות, סדרת עסקאות ותקשורת ' +
          'לרשומת SAR חתומה עם שרשרת שמירה שלמה. עמידה ב-MAR Art. 16 ו-FINRA 3110.',
        starter_template:
          'שני חוזים חתומים: surveillance-alert-packager → sar-bundler. ' +
          'חיבור מערכת המעקב; חבילת SAR מוכנה עם ההתראה.',
        overview: {
          pain:
            'כשהתראת מעקב מתפעלת, צוות הציות צריך לאסוף נתוני ספר פקודות, ' +
            'סדרת עסקאות ותקשורות לחבילת SAR — אך עד שהאסיפה הידנית מסתיימת, ' +
            'שרשרת השמירה שבורה והראיות נפסלות בבית משפט.',
          solution:
            'חבילת ראיות התראת מעקב™ מופעלת אוטומטית עם ההתראה ואורזת תמונת ספר פקודות, ' +
            'סדרת עסקאות מתואמת ותקשורות לחבילת SAR חתומה אחת עם שרשרת שמירה קריפטוגרפית שלמה.',
          how_it_works: [
            'חיבור פלטפורמת המעקב להפעלת הקונדקטור בהתראה',
            'המנוע לוכד תמונת ספר פקודות, סדרת עסקאות ותקשורות ברגע ההתראה',
            'כל הראיות חתומות עם Ed25519 וחותמות RFC 3161',
            'חבילת SAR עם שרשרת שמירה שלמה מוכנה לרגולטורים',
          ],
          who_for:
            'ראשי מעקב שוק, יועצים משפטיים לציות ו-MLRO בברוקרים, בורסות ובנקי השקעות ' +
            'הכפופים לדרישות דיווח פעילות חשודה לפי MAR Art. 16 ו-FINRA Rule 3110.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmCsdSettlementFailEvidence as EntityId,
    slug: 'cm-csd-settlement-fail-evidence',
    domain: 'Financial' as DomainType,
    price_usd: 15000,
    monthly_price_usd: 19500,
    contracts_count: 1,
    inputs: [{ name: 'settlement_fail_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'fail_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Settlement Fail Evidence™',
        tagline: 'CSDR Art. 7 documentation signed at the moment of detection.',
        description:
          'Documents every settlement failure with tamper-evident evidence: ' +
          'counterparty LEI, ISIN, shortfall, buy-in attempt, and CSDR penalty ' +
          'calculation — all signed at the moment of detection.',
        starter_template:
          'Single signed contract: settlement-fail-recorder. ' +
          'Connect your settlement system; CSD disputes resolve against signed records.',
        overview: {
          pain:
            'CSDR Art. 7 imposes mandatory buy-ins and cash penalties for settlement ' +
            'failures, but disputes about when a fail was detected and what the ' +
            'shortfall was are common. Reconstructing the timeline retroactively ' +
            'from fragmented records rarely holds up against the CSD.',
          solution:
            'Settlement Fail Evidence™ documents every settlement failure at the ' +
            'moment of detection — counterparty LEI, ISIN, shortfall quantity, ' +
            'buy-in attempt, and CSDR penalty calculation — all signed with a ' +
            'tamper-evident timestamp that is beyond dispute.',
          how_it_works: [
            'Connect your settlement system or CSD feed to the conductor',
            'Engine signs the fail record at detection with full counterparty and instrument detail',
            'Buy-in attempt and CSDR penalty calculation are captured and co-signed',
            'Disputes resolved against the signed record; no retroactive reconstruction needed',
          ],
          who_for:
            'Settlement operations managers, post-trade compliance officers, and ' +
            'legal teams at custodians, broker-dealers, and asset managers subject ' +
            'to CSDR settlement discipline regime requirements.',
        },
      },
      he: {
        name: 'ראיות כשל סליקה™',
        tagline: 'תיעוד CSDR Art. 7 חתום ברגע הגילוי.',
        description:
          'מתעד כל כשל סליקה עם ראיות חסינות מניפולציה: LEI צד נגדי, ISIN, מחסור, ' +
          'ניסיון buy-in וחישוב עונש CSDR — הכל חתום ברגע הגילוי.',
        starter_template:
          'חוזה חתום אחד: settlement-fail-recorder. ' +
          'חיבור מערכת הסליקה; סכסוכי CSD נפתרים מול רשומות חתומות.',
        overview: {
          pain:
            'CSDR Art. 7 מטיל buy-ins חובה וקנסות מזומן על כשלי סליקה, אך סכסוכים על ' +
            'מועד גילוי הכשל וגודל המחסור נפוצים. שחזור הציר הזמני בדיעבד מרשומות מפוצלות ' +
            'לעיתים נדירות עומד מול ה-CSD.',
          solution:
            'ראיות כשל סליקה™ מתעדת כל כשל סליקה ברגע הגילוי — LEI צד נגדי, ISIN, ' +
            'כמות מחסור, ניסיון buy-in וחישוב עונש CSDR — הכל חתום עם חותמת זמן חסינת מניפולציה.',
          how_it_works: [
            'חיבור מערכת הסליקה או זרם CSD לקונדקטור',
            'המנוע חותם את רשומת הכשל בגילוי עם פרטי צד נגדי ומכשיר מלאים',
            'ניסיון buy-in וחישוב עונש CSDR נלכדים וחתומים-משותפים',
            'סכסוכים נפתרים מול הרשומה החתומה; אין צורך בשחזור רטרואקטיבי',
          ],
          who_for:
            'מנהלי תפעול סליקה, קציני ציות פוסט-מסחר ויועצים משפטיים באמינות, ' +
            'ברוקרים ומנהלי נכסים הכפופים למשטר משמעת הסליקה של CSDR.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmDoraIctIncidentPack as EntityId,
    slug: 'cm-dora-ict-incident-pack',
    domain: 'Financial' as DomainType,
    price_usd: 20000,
    monthly_price_usd: 26000,
    contracts_count: 2,
    inputs: [{ name: 'ict_incident', data_type: 'Stream', required: true }],
    outputs: [{ name: 'dora_notification_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'DORA ICT Incident Pack™',
        tagline: '4-hour NCA notification — signed, classified, delivered.',
        description:
          'Classifies ICT incidents against DORA Art. 18 thresholds and generates ' +
          'signed 4-hour / 72-hour / 1-month NCA notification bundles. ' +
          'The clock starts at detection, not when compliance finished debating.',
        starter_template:
          'Two signed contracts: dora-classifier → ict-incident-reporter. ' +
          'Wire your ITSM; DORA notification bundles on incident detection.',
        overview: {
          pain:
            'DORA requires financial entities to notify their National Competent ' +
            'Authority within 4 hours of detecting a major ICT incident — but most ' +
            'firms spend those hours debating classification, not drafting the ' +
            'notification. Late or incomplete submissions trigger supervisory action.',
          solution:
            'DORA ICT Incident Pack™ classifies every ICT incident against DORA ' +
            'Art. 18 thresholds at detection time and automatically generates ' +
            'signed 4-hour, 72-hour, and 1-month NCA notification bundles. The ' +
            'clock starts at detection, not at the compliance team\'s next meeting.',
          how_it_works: [
            'Wire your ITSM or monitoring platform to trigger the conductor on incident',
            'Engine classifies incident against DORA Art. 18 severity thresholds instantly',
            'Signed 4-hour notification bundle generated and queued for NCA submission',
            '72-hour and 1-month follow-up bundles auto-generated on schedule',
          ],
          who_for:
            'CISOs, IT Risk officers, and Operational Resilience leads at banks, ' +
            'payment institutions, investment firms, and critical ICT third-party ' +
            'providers subject to EU DORA requirements from January 2025.',
        },
      },
      he: {
        name: 'חבילת אירוע ICT של DORA™',
        tagline: 'הודעה ל-NCA תוך 4 שעות — חתומה, מסווגת, מוגשת.',
        description:
          'מסווגת אירועי ICT מול סף DORA Art. 18 ומייצרת חבילות הודעה חתומות ' +
          'ל-NCA תוך 4 שעות / 72 שעות / חודש. השעון מתחיל בגילוי.',
        starter_template:
          'שני חוזים חתומים: dora-classifier → ict-incident-reporter. ' +
          'חיבור ITSM; חבילות הודעה DORA עם גילוי אירוע.',
        overview: {
          pain:
            'DORA מחייב גופים פיננסיים להודיע לרשות המוסמכת הלאומית תוך 4 שעות מגילוי ' +
            'אירוע ICT מהותי — אך רוב החברות מבלות שעות אלה בוויכוח על סיווג, לא בניסוח ההודעה.',
          solution:
            'חבילת אירוע ICT של DORA™ מסווגת כל אירוע ICT מול סף DORA Art. 18 בגילוי ' +
            'ומייצרת אוטומטית חבילות הודעה חתומות ל-NCA תוך 4 שעות, 72 שעות וחודש. ' +
            'השעון מתחיל בגילוי.',
          how_it_works: [
            'חיבור ITSM או פלטפורמת ניטור להפעלת הקונדקטור באירוע',
            'המנוע מסווג אירוע מול סף חומרת DORA Art. 18 מיידית',
            'חבילת הודעת 4 שעות חתומה נוצרת ומועברת להגשה ל-NCA',
            'חבילות המשך 72 שעות וחודש נוצרות אוטומטית לפי לוח זמנים',
          ],
          who_for:
            'CISO, קציני סיכון IT וראשי חוסן תפעולי בבנקים, מוסדות תשלום, ' +
            'חברות השקעה וספקי ICT קריטיים צד שלישי הכפופים לדרישות DORA מינואר 2025.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmIsaMagnaIxbrlFiling as EntityId,
    slug: 'cm-isa-magna-ixbrl-filing',
    domain: 'Financial' as DomainType,
    price_usd: 12000,
    monthly_price_usd: 15600,
    contracts_count: 2,
    inputs: [{ name: 'ixbrl_document', data_type: 'Document', required: true }],
    outputs: [{ name: 'magna_submission_bundle', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'ISA MAGNA iXBRL Filing™',
        tagline: 'Validated, signed iXBRL bundle — MAGNA-ready for Israeli listed companies.',
        description:
          'Validates iXBRL against ISA taxonomy, checks IFRS disclosure completeness ' +
          'and balance sheet equation, then packages the signed bundle for MAGNA ' +
          'submission. Built for TASE-listed companies.',
        starter_template:
          'Two signed contracts: ixbrl-validator → magna-packager. ' +
          'Validate before you submit; ISA error notices stop.',
        overview: {
          pain:
            'TASE-listed companies submit iXBRL filings to the ISA MAGNA portal ' +
            'under tight reporting deadlines. A single taxonomy error or missing IFRS ' +
            'disclosure triggers an ISA error notice, a corrective filing, and ' +
            'potential enforcement — all after the public has already seen the document.',
          solution:
            'ISA MAGNA iXBRL Filing™ validates iXBRL against the current ISA taxonomy, ' +
            'checks IFRS disclosure completeness and balance sheet equation parity, ' +
            'then packages a signed, MAGNA-ready bundle — catching every error before ' +
            'the submission window closes.',
          how_it_works: [
            'Feed your iXBRL document to the conductor before the MAGNA deadline',
            'Engine validates against ISA taxonomy and IFRS disclosure requirements',
            'Balance sheet equation and cross-reference checks run automatically',
            'Signed, MAGNA-ready bundle output — submit with confidence',
          ],
          who_for:
            'CFOs, financial controllers, and external auditors at TASE-listed ' +
            'companies and their reporting agents preparing periodic iXBRL ' +
            'financial disclosures for ISA MAGNA submission.',
        },
      },
      he: {
        name: 'הגשת iXBRL ל-MAGNA של רשות ניירות ערך™',
        tagline: 'חבילת iXBRL מאומתת וחתומה — מוכנה ל-MAGNA לחברות ישראליות רשומות.',
        description:
          'מאמת iXBRL מול טקסונומיית רש"ן, בודק שלמות גילויי IFRS ומשוואת מאזן, ' +
          'ואורז את החבילה החתומה להגשה ל-MAGNA. בנוי לחברות רשומות בבורסת תל אביב.',
        starter_template:
          'שני חוזים חתומים: ixbrl-validator → magna-packager. ' +
          'אימות לפני הגשה; הודעות שגיאה מרש"ן מפסיקות.',
        overview: {
          pain:
            'חברות רשומות בבורסת תל אביב מגישות דוחות iXBRL לפורטל MAGNA של רש"ן בלוחות ' +
            'זמנים קצרים. שגיאת טקסונומיה אחת או גילוי IFRS חסר מובילים להודעת שגיאה, ' +
            'הגשה מתקנת ואכיפה פוטנציאלית.',
          solution:
            'הגשת iXBRL ל-MAGNA™ מאמתת iXBRL מול טקסונומיית רש"ן העדכנית, בודקת שלמות ' +
            'גילויי IFRS ומשוואת מאזן, ואורזת חבילה חתומה ומוכנה ל-MAGNA — לפני סגירת חלון ההגשה.',
          how_it_works: [
            'הזנת מסמך iXBRL לקונדקטור לפני מועד MAGNA',
            'המנוע מאמת מול טקסונומיית רש"ן ודרישות גילוי IFRS',
            'בדיקות משוואת מאזן והפניות צולבות מתבצעות אוטומטית',
            'חבילה חתומה ומוכנה ל-MAGNA נוצרת — הגשה בביטחון',
          ],
          who_for:
            'מנהלי כספים, בקרי פיננסים ורואי חשבון חיצוניים בחברות רשומות בבורסת תל אביב ' +
            'ובסוכני הדיווח שלהם המכינים גילויים פיננסיים תקופתיים ב-iXBRL להגשה ל-MAGNA.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmTokenizedBondIssuance as EntityId,
    slug: 'cm-tokenized-bond-issuance',
    domain: 'Financial' as DomainType,
    price_usd: 30000,
    monthly_price_usd: 39000,
    contracts_count: 2,
    inputs: [{ name: 'issuance_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'issuance_attestation', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Tokenized Bond Issuance™',
        tagline: 'MiCA and DLT Pilot Regime attestation for tokenized bond issuance.',
        description:
          'Validates LEI, ISIN, prospectus hash, smart-contract address, and investor ' +
          'eligibility against MiCA and the EU DLT Pilot Regime. Signs the issuance ' +
          'record for NCA inspection.',
        starter_template:
          'Two signed contracts: bond-terms-validator → issuance-attestor. ' +
          'Link on-chain issuance to off-chain regulatory record in one run.',
        overview: {
          pain:
            'Tokenized bond issuance sits at the intersection of blockchain and ' +
            'securities law — issuers must satisfy MiCA, the EU DLT Pilot Regime, ' +
            'and conventional prospectus rules simultaneously. Without a verifiable ' +
            'link between the on-chain token and the off-chain regulatory record, ' +
            'NCA inspection requests become unresolvable.',
          solution:
            'Tokenized Bond Issuance™ validates LEI, ISIN, prospectus hash, smart- ' +
            'contract address, and investor eligibility in a single conductor run, ' +
            'then signs the issuance record — creating a tamper-evident bridge ' +
            'between on-chain token and off-chain regulatory obligation.',
          how_it_works: [
            'Submit issuance event with prospectus hash and smart-contract address',
            'Engine validates LEI, ISIN, prospectus integrity, and investor eligibility',
            'Issuance record is signed and linked to the on-chain token identifier',
            'NCA inspection bundle ready; on-chain and off-chain records verifiably matched',
          ],
          who_for:
            'Issuers, capital markets lawyers, and compliance officers at digital ' +
            'asset firms, investment banks, and fintech platforms issuing tokenized ' +
            'debt instruments under EU MiCA or the DLT Pilot Regime framework.',
        },
      },
      he: {
        name: 'הנפקת אג"ח טוקנייזד™',
        tagline: 'אישור MiCA ומשטר ניסוי DLT להנפקת אג"ח טוקנייזד.',
        description:
          'מאמת LEI, ISIN, hash תשקיף, כתובת חוזה חכם וכשירות משקיעים מול MiCA ו-DLT Pilot Regime. ' +
          'חותם את רשומת ההנפקה לבדיקת הרגולטור הלאומי.',
        starter_template:
          'שני חוזים חתומים: bond-terms-validator → issuance-attestor. ' +
          'קישור הנפקה on-chain לרשומה רגולטורית off-chain בהרצה אחת.',
        overview: {
          pain:
            'הנפקת אג"ח טוקנייזד עומדת בצומת של בלוקצ\'ין ודיני ניירות ערך — המנפיקים ' +
            'חייבים לעמוד במקביל ב-MiCA, במשטר ניסוי DLT ובכללי תשקיף קונבנציונליים. ' +
            'ללא קישור ניתן לאימות בין הטוקן on-chain לרשומה הרגולטורית off-chain, ' +
            'בקשות בדיקה רגולטוריות הופכות לבלתי פתירות.',
          solution:
            'הנפקת אג"ח טוקנייזד™ מאמתת LEI, ISIN, hash תשקיף, כתובת חוזה חכם וכשירות משקיעים ' +
            'בהרצה אחת, ואז חותמת את רשומת ההנפקה — ויוצרת גשר חסין מניפולציה בין הטוקן לחובה הרגולטורית.',
          how_it_works: [
            'הגשת אירוע הנפקה עם hash תשקיף וכתובת חוזה חכם',
            'המנוע מאמת LEI, ISIN, שלמות תשקיף וכשירות משקיעים',
            'רשומת ההנפקה נחתמת ומקושרת למזהה הטוקן on-chain',
            'חבילת בדיקת NCA מוכנה; רשומות on-chain ו-off-chain מואמתות בצלב',
          ],
          who_for:
            'מנפיקים, עורכי דין שוק הון וקציני ציות בחברות נכסים דיגיטליים, בנקי השקעות ' +
            'ופלטפורמות פינטק המנפיקות מכשירי חוב טוקנייזד תחת MiCA האירופי או מסגרת DLT Pilot Regime.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmProxyVoteReceipt as EntityId,
    slug: 'cm-proxy-vote-receipt',
    domain: 'Financial' as DomainType,
    price_usd: 8000,
    monthly_price_usd: 10400,
    contracts_count: 1,
    inputs: [{ name: 'vote_instruction', data_type: 'Document', required: true }],
    outputs: [{ name: 'vote_receipt', data_type: 'Document', required: true }],
    copy: {
      en: {
        name: 'Proxy Vote Receipt™',
        tagline: 'Tamper-evident signed receipt for every shareholder proxy vote.',
        description:
          'Validates entitlement, AGM deadline, and vote direction. Signs the ' +
          'receipt at submission. Proves the vote reached the issuer unchanged. ' +
          'Satisfies EU SRD II Art. 9c and SEC Rule 14a-16.',
        starter_template:
          'Single signed contract: proxy-vote-recorder. ' +
          'Route each vote instruction; disputes resolve against a signed receipt.',
        overview: {
          pain:
            'Proxy vote disputes at AGMs — especially on contentious resolutions — ' +
            'require evidence that each institutional vote instruction was received ' +
            'by the issuer unmodified and before the deadline. Paper confirmations ' +
            'and email trails are frequently inconclusive or unavailable.',
          solution:
            'Proxy Vote Receipt™ validates entitlement, AGM deadline compliance, ' +
            'and vote direction at submission, then signs a tamper-evident receipt ' +
            'proving the vote reached the issuer unchanged — satisfying EU SRD II ' +
            'Art. 9c and SEC Rule 14a-16 end-to-end.',
          how_it_works: [
            'Route each vote instruction through the conductor at submission',
            'Engine validates entitlement, AGM deadline, and vote direction',
            'Signed receipt is issued per vote: Ed25519 + timestamp',
            'Disputes resolved instantly against the signed receipt — no reconstruction needed',
          ],
          who_for:
            'Proxy advisors, corporate secretaries, and institutional investor ' +
            'voting desks at custodian banks and asset managers handling proxy ' +
            'voting for EU and US listed companies under SRD II and SEC rules.',
        },
      },
      he: {
        name: 'קבלת הצבעת פרוקסי™',
        tagline: 'קבלה חתומה חסינת מניפולציה לכל הצבעת פרוקסי של בעל מניות.',
        description:
          'מאמת זכאות, מועד אחרון של אסיפה כללית וכיוון הצבעה. חותם את הקבלה בהגשה. ' +
          'מוכיח שההצבעה הגיעה למנפיק ללא שינוי. עמידה ב-SRD II Art. 9c ו-SEC Rule 14a-16.',
        starter_template:
          'חוזה חתום אחד: proxy-vote-recorder. ' +
          'ניתוב כל הוראת הצבעה; סכסוכים נפתרים מול קבלה חתומה.',
        overview: {
          pain:
            'סכסוכי הצבעת פרוקסי באסיפות כלליות — במיוחד בהחלטות שנויות במחלוקת — ' +
            'מחייבים ראיות שכל הוראת הצבעה מוסדית התקבלה אצל המנפיק ללא שינוי ולפני המועד. ' +
            'אישורים בנייר ושרשרות מייל אינם חד-משמעיים לעיתים קרובות.',
          solution:
            'קבלת הצבעת פרוקסי™ מאמתת זכאות, עמידה במועד ה-AGM וכיוון הצבעה בהגשה, ' +
            'ואז חותמת קבלה חסינת מניפולציה המוכיחה שההצבעה הגיעה ללא שינוי — ' +
            'בעמידה ב-SRD II Art. 9c ו-SEC Rule 14a-16.',
          how_it_works: [
            'ניתוב כל הוראת הצבעה דרך הקונדקטור בהגשה',
            'המנוע מאמת זכאות, מועד AGM וכיוון הצבעה',
            'קבלה חתומה מונפקת לכל הצבעה: Ed25519 + חותמת זמן',
            'סכסוכים נפתרים מיידית מול הקבלה החתומה — אין צורך בשחזור',
          ],
          who_for:
            'יועצי פרוקסי, מזכירים ארגוניים ושולחנות הצבעה מוסדיים בבנקי משמורת ' +
            'ומנהלי נכסים המטפלים בהצבעת פרוקסי לחברות רשומות בארה"ב ובאירופה.',
        },
      },
    },
  },
  // ── Capital markets commercial flagships (§8.6 — three SaaS products) ──
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmComplianceVault as EntityId,
    slug: 'cm-compliance-vault',
    domain: 'Financial' as DomainType,
    price_usd: null,
    monthly_price_usd: null,
    contracts_count: 2,
    inputs: [{ name: 'comm_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'retention_attestation', data_type: 'Document', required: true }],
    enterprise: true,
    copy: {
      en: {
        name: 'CM Compliance Vault™',
        tagline: 'Every regulated communication, proven.',
        description:
          'Cryptographic proof of surveillance capture, review, and retention for ' +
          'every regulated communication — voice, chat, email, FIX order messages — ' +
          'across MiFID II Article 16, FINRA Rule 3110, and SEC Rule 17a-4. Every ' +
          'captured item is Ed25519-signed at ingestion, hash-chained, RFC 3161 ' +
          'timestamped, and surfaced in a CCO dashboard.',
        starter_template:
          'Two signed contracts: communication-capture → retention-verifier. ' +
          'Wire your Symphony / Bloomberg Chat / Teams feeds once.',
        overview: {
          pain:
            'Regulators across MiFID II, FINRA, and SEC rules demand tamper-proof ' +
            'evidence that every trade-related communication was captured, reviewed, ' +
            'and retained for the full statutory period. A single SEC or FCA examination ' +
            'that exposes a gap in surveillance capture routinely results in seven-figure ' +
            'penalties and personal liability for the CCO.',
          solution:
            'CM Compliance Vault™ signs every regulated communication — voice, chat, ' +
            'email, FIX order messages — at ingestion with Ed25519 and RFC 3161 ' +
            'timestamps, chains all records cryptographically, and surfaces the full ' +
            'surveillance lifecycle in a real-time CCO dashboard. One connection, ' +
            'three regimes, zero gaps.',
          how_it_works: [
            'Connect Symphony, Bloomberg Chat, Teams, email, and FIX gateways once',
            'Every message is signed at ingestion — timestamp, source, and content hash locked in',
            'Review workflow captures supervisor sign-off as a co-signed attestation',
            'CCO dashboard shows real-time coverage across all channels; regulator exports in one click',
          ],
          who_for:
            'Chief Compliance Officers, eComms surveillance leads, and General ' +
            'Counsel at broker-dealers, hedge funds, and investment banks operating ' +
            'across MiFID II, FINRA Rule 3110, and SEC Rule 17a-4 jurisdictions.',
        },
      },
      he: {
        name: 'CM Compliance Vault™',
        tagline: 'כל תקשורת רגולטורית — מוכחת.',
        description:
          'הוכחה קריפטוגרפית של לכידה, ביקורת ושמירה לכל תקשורת רגולטורית — קול, ' +
          'צ\'אט, אימייל, הודעות FIX — לפי MiFID II סעיף 16, FINRA Rule 3110, ' +
          'ו-SEC Rule 17a-4. כל פריט נחתם ב-Ed25519 בקליטה, משורשר בהאש, ' +
          'חתום-זמן RFC 3161, ומוצג בלוח-מחוונים של ה-CCO.',
        starter_template:
          'שני חוזים חתומים: communication-capture → retention-verifier. ' +
          'חיבור פעם אחת ל-Symphony / Bloomberg / Teams.',
        overview: {
          pain:
            'רגולטורים לפי MiFID II, FINRA ו-SEC דורשים ראיות חסינות מניפולציה ' +
            'שכל תקשורת הקשורה למסחר נלכדה, נסקרה ונשמרה לאורך כל תקופת השמירה החוקית. ' +
            'בדיקת SEC או FCA אחת שחושפת פער בלכידת מעקב גוררת קנסות ממיליון דולר ' +
            'ואחריות אישית של ה-CCO.',
          solution:
            'CM Compliance Vault™ חותמת כל תקשורת רגולטורית — קול, צ\'אט, אימייל, הודעות FIX — ' +
            'בקליטה עם Ed25519 וחותמות RFC 3161, משרשרת את כל הרשומות קריפטוגרפית, ' +
            'ומציגה את מחזור חיי המעקב המלא בלוח מחוונים CCO בזמן אמת. ' +
            'חיבור אחד, שלושה משטרים, אפס פערים.',
          how_it_works: [
            'חיבור Symphony, Bloomberg Chat, Teams, מייל ושערי FIX פעם אחת',
            'כל הודעה נחתמת בקליטה — חותמת זמן, מקור ו-hash תוכן נעולים',
            'תהליך סקירה לוכד אישור מפקח כאישור חתום-משותף',
            'לוח CCO מציג כיסוי בזמן אמת; ייצוא לרגולטורים בלחיצה אחת',
          ],
          who_for:
            'מנהלי ציות ראשיים, ראשי מעקב תקשורות אלקטרוניות ויועצים כלליים ' +
            'בברוקרים, קרנות גידור ובנקי השקעות הפועלים תחת MiFID II, FINRA Rule 3110 ' +
            'ו-SEC Rule 17a-4.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmModelNavProvenance as EntityId,
    slug: 'cm-model-nav-provenance',
    domain: 'Financial' as DomainType,
    price_usd: null,
    monthly_price_usd: null,
    contracts_count: 3,
    inputs: [{ name: 'model_artifact', data_type: 'Document', required: true }],
    outputs: [{ name: 'nav_attestation_chain', data_type: 'Document', required: true }],
    enterprise: true,
    copy: {
      en: {
        name: 'CM Model & NAV Provenance™',
        tagline: 'Every alpha signal, every NAV calculation — proven.',
        description:
          'Cryptographic provenance for quantitative models and fund NAV calculations ' +
          'across the full lifecycle — research notebook commit through deployment to ' +
          'daily valuation. Every model version is Ed25519-signed with inputs, ' +
          'parameters, and back-test results; every NAV chain is RFC 3161 timestamped. ' +
          'Maps to SR 11-7, PRA SS1/23, and CSSF NAV materiality thresholds.',
        starter_template:
          'Three signed contracts: model-registry → nav-calculator → provenance-verifier. ' +
          'Onboard your back-test pipeline once; LP letters write themselves.',
        overview: {
          pain:
            'Regulators, LPs, and auditors increasingly demand proof that a fund\'s ' +
            'NAV was computed from a specific, unmodified model version using the ' +
            'pricing inputs that existed at calculation time. Without cryptographic ' +
            'lineage spanning model research through daily valuation, due diligence ' +
            'responses and supervisory reviews become months-long reconstruction exercises.',
          solution:
            'CM Model & NAV Provenance™ creates an unbroken cryptographic chain from ' +
            'research notebook commit to daily NAV — every model version signed with ' +
            'its inputs and back-test results, every valuation anchored to a ' +
            'timestamped pricing source. One onboarding; LP and regulatory queries ' +
            'answered in minutes with verifiable evidence.',
          how_it_works: [
            'Register each model promotion: code, parameters, and back-test results signed',
            'NAV calculation ingests signed pricing sources; independent re-derivation runs in parallel',
            'Daily NAV chain is RFC 3161 timestamped and linked to the originating model version',
            'LP queries and supervisory reviews answered with a verifiable provenance bundle',
          ],
          who_for:
            'CIOs, model risk teams, and fund CFOs at quantitative hedge funds, ' +
            'asset managers, and investment banks with systematic strategies subject ' +
            'to Fed SR 11-7, PRA SS1/23, or CSSF NAV materiality rules.',
        },
      },
      he: {
        name: 'CM Model & NAV Provenance™',
        tagline: 'כל איתות אלפא, כל חישוב NAV — מוכח.',
        description:
          'מקור קריפטוגרפי למודלים כמותיים ולחישובי NAV של קרנות לאורך כל מחזור החיים. ' +
          'כל גרסת מודל חתומה ב-Ed25519 עם קלטים, פרמטרים ותוצאות back-test; שרשרת ' +
          'חישוב ה-NAV חתומה-זמן RFC 3161. מיפוי ל-SR 11-7, PRA SS1/23 וספי מהותיות ' +
          'NAV של CSSF.',
        starter_template:
          'שלושה חוזים חתומים: model-registry → nav-calculator → provenance-verifier.',
        overview: {
          pain:
            'רגולטורים, שותפים מוגבלים ומבקרים דורשים יותר ויותר הוכחה שה-NAV של קרן ' +
            'חושב מגרסת מודל ספציפית ובלתי מתוקנת תוך שימוש בקלטי תמחור שהתקיימו בזמן החישוב. ' +
            'ללא מקור קריפטוגרפי ממחקר ועד הערכה יומית, תגובות לבדיקות נאותות הופכות ' +
            'לתרגילי שחזור בני חודשים.',
          solution:
            'CM Model & NAV Provenance™ יוצרת שרשרת קריפטוגרפית בלתי שבורה ממחויבות ' +
            'מחברת המחקר ועד ה-NAV היומי — כל גרסת מודל חתומה עם קלטיה ותוצאות back-test, ' +
            'כל הערכה מעוגנת במקור תמחור חתום-זמן. עליה אחת; שאלות LP ורגולטוריות נענות ' +
            'תוך דקות עם ראיות ניתנות לאימות.',
          how_it_works: [
            'רישום כל קידום מודל: קוד, פרמטרים ותוצאות back-test חתומים',
            'חישוב NAV קולט מקורות תמחור חתומים; גזירה מחודשת עצמאית רצה במקביל',
            'שרשרת NAV יומית חתומה-זמן RFC 3161 ומקושרת לגרסת המודל המקורית',
            'שאלות LP ובדיקות פיקוח נענות עם חבילת מקור ניתנת לאימות',
          ],
          who_for:
            'CIO, צוותי סיכוני מודל ומנהלי כספים של קרנות בקרנות גידור כמותיות, ' +
            'מנהלי נכסים ובנקי השקעות עם אסטרטגיות שיטתיות הכפופים ל-SR 11-7, ' +
            'PRA SS1/23 או כללי מהותיות NAV של CSSF.',
        },
      },
    },
  },
  {
    root_id: OFFICIAL_PRODUCT_IDS.cmTradeReconstructionSuite as EntityId,
    slug: 'cm-trade-reconstruction-suite',
    domain: 'Financial' as DomainType,
    price_usd: null,
    monthly_price_usd: null,
    contracts_count: 3,
    inputs: [{ name: 'trade_lifecycle_event', data_type: 'Stream', required: true }],
    outputs: [{ name: 'reconstruction_bundle', data_type: 'Document', required: true }],
    enterprise: true,
    copy: {
      en: {
        name: 'CM Trade Reconstruction Suite™',
        tagline: 'Every order, every fill, every hop — provable.',
        description:
          'Cryptographic reconstruction of the full trade lifecycle — pre-trade order ' +
          'intent through FIX execution reports to post-trade settlement — for MAR ' +
          'Article 7 market-abuse investigations, MiFID II best-execution analysis, ' +
          'and CFTC Regulation AT audit trails. Microsecond RFC 3161 timestamps and ' +
          'tamper-evident lineage.',
        starter_template:
          'Three signed contracts: pre-trade-intent → fix-execution-validator → ' +
          'post-trade-reconstructor. Connect OMS/EMS + FIX gateway once.',
        overview: {
          pain:
            'Market abuse investigations under MAR Article 7 require firms to ' +
            'reconstruct the full trade lifecycle — from pre-trade order intent through ' +
            'every FIX execution hop to post-trade settlement — in microsecond detail. ' +
            'Assembling this from three or more siloed systems after a regulator demands ' +
            'it takes months and the gaps invite enforcement action.',
          solution:
            'CM Trade Reconstruction Suite™ cryptographically reconstructs the full ' +
            'lifecycle — pre-trade intent, FIX execution reports, and post-trade ' +
            'settlement — in a single connected conductor pipeline. Microsecond RFC 3161 ' +
            'timestamps and tamper-evident lineage satisfy MAR Art. 7, MiFID II best ' +
            'execution, and CFTC Regulation AT simultaneously.',
          how_it_works: [
            'Connect OMS, EMS, and FIX gateway to the three-stage conductor once',
            'Pre-trade intent is signed at order origination with algo parameters',
            'Every FIX execution report is signed and linked to the originating intent',
            'Post-trade settlement attestation closes the chain; full lifecycle in one verifiable bundle',
          ],
          who_for:
            'Heads of Market Surveillance, MAR compliance officers, and trading ' +
            'operations leads at investment banks, broker-dealers, and systematic ' +
            'trading firms subject to MAR, MiFID II, and CFTC Regulation AT.',
        },
      },
      he: {
        name: 'CM Trade Reconstruction Suite™',
        tagline: 'כל הוראה, כל ביצוע, כל קפיצה — ניתן להוכחה.',
        description:
          'שחזור קריפטוגרפי של מחזור החיים המלא של עסקה — מכוונה לפני-עסקה דרך ' +
          'דיווחי ביצוע FIX ועד אישור סליקה לאחר-עסקה — לחקירות MAR סעיף 7, ניתוח ' +
          'ביצוע מיטבי MiFID II, ומסלולי ביקורת CFTC Regulation AT. חתימות RFC 3161 ' +
          'ברזולוציית מיקרו-שניות.',
        starter_template:
          'שלושה חוזים חתומים: pre-trade-intent → fix-execution-validator → ' +
          'post-trade-reconstructor.',
        overview: {
          pain:
            'חקירות ניצול שוק לפי MAR סעיף 7 מחייבות חברות לשחזר את מחזור החיים המלא של עסקה — ' +
            'מכוונת הוראה לפני-עסקה דרך כל קפיצת ביצוע FIX ועד סליקה — ברזולוציית מיקרו-שניות. ' +
            'איסוף זה משלוש מערכות מבודדות לאחר דרישת רגולטור לוקח חודשים.',
          solution:
            'CM Trade Reconstruction Suite™ משחזרת קריפטוגרפית את מחזור החיים המלא — ' +
            'כוונה לפני-עסקה, דיווחי ביצוע FIX ואישור סליקה לאחר-עסקה — בצינור קונדקטור ' +
            'מחובר אחד. חותמות RFC 3161 ברזולוציית מיקרו-שניות עומדות ב-MAR, MiFID II ' +
            'ו-CFTC Regulation AT במקביל.',
          how_it_works: [
            'חיבור OMS, EMS ושער FIX לקונדקטור שלושת השלבים פעם אחת',
            'כוונה לפני-עסקה נחתמת ביצירת הוראה עם פרמטרי אלגוריתם',
            'כל דיווח ביצוע FIX נחתם ומקושר לכוונה המקורית',
            'אישור סליקה לאחר-עסקה סוגר את השרשרת; מחזור חיים מלא בחבילה ניתנת לאימות',
          ],
          who_for:
            'ראשי מעקב שוק, קציני ציות MAR וראשי תפעול מסחר בבנקי השקעות, ברוקרים ' +
            'ובתי מסחר שיטתיים הכפופים ל-MAR, MiFID II ו-CFTC Regulation AT.',
        },
      },
    },
  },
];

function asLineage(rootId: EntityId): Lineage {
  return { root_id: rootId, entries: [] };
}

function asContract(p: OfficialProduct): BehavioralContract {
  return {
    inputs: p.inputs,
    outputs: p.outputs,
    parameters: [],
    behavior_rules: [],
    domain_adapter: p.domain,
    macros: [],
  };
}

/** Build a `MarketplaceListing` view of an official product for the
 *  given locale. Reads the per-locale display copy and fills the
 *  remaining listing fields with stable, sensible defaults so the
 *  card / detail page render without conditional fallbacks. */
function buildListing(p: OfficialProduct, locale: Locale): MarketplaceListing {
  const copy = p.copy[locale] ?? p.copy.en;
  return {
    root_id: p.root_id,
    version: '1.0.0',
    name: copy.name,
    tagline: copy.tagline,
    description: copy.description,
    domain: p.domain,
    author_name: 'Growing Intelligence Ltd.',
    author_public_key: GI_AUTHOR_PUBKEY,
    published_at: PUBLISHED_AT_FIXED,
    // Enterprise-tier products (`price_usd === null`) are filtered out of
    // marketplace listings before this is reached, but coerce defensively
    // so the type contract on `MarketplaceListing` holds.
    price_usd: p.price_usd ?? 0,
    rating: 5,
    review_count: 0,
    installs: 0,
    certification: 'gold',
    contract: asContract(p),
    lineage: asLineage(p.root_id),
  };
}

/** Returns the official products as `MarketplaceListing` rows in the
 *  given locale. Order is stable: matches the user-facing product
 *  list (gate-to-shelf, AMAR, research, citizen, cyber, legal). */
export function getOfficialListings(locale: Locale): MarketplaceListing[] {
  // Enterprise-tier products (Capital Markets flagships) are sold via
  // Contact Sales on the website, not the in-app self-serve marketplace.
  // They appear in OFFICIAL_PRODUCTS for catalog/SEO purposes but are
  // filtered out of marketplace listings.
  return OFFICIAL_PRODUCTS.filter((p) => p.price_usd !== null).map((p) =>
    buildListing(p, locale),
  );
}

/** Lookup one official listing by id. Returns null when the id is
 *  not in the official set. Enterprise-tier (Contact Sales) products
 *  resolve here so a direct deep-link to `/marketplace/<id>` renders
 *  the detail page with the full Overview block — they are filtered
 *  out of the marketplace grid in [`getOfficialListings`] but still
 *  navigate-able from anywhere that ships their root_id. The
 *  detail-page price strip branches on `price_usd === null` to
 *  render "Contact Sales" in place of the install CTA. */
export function getOfficialListing(
  id: string,
  locale: Locale,
): MarketplaceListing | null {
  const product = OFFICIAL_PRODUCTS.find((p) => p.root_id === id);
  if (!product) return null;
  return buildListing(product, locale);
}

/** True when `id` is in the curated official-products set. The card
 *  uses this to show the "Built by Growing Intelligence" badge. */
export function isOfficialProduct(id: string): boolean {
  return OFFICIAL_PRODUCT_ID_SET.has(id as EntityId);
}

/** Find the slug for an official product id. Used by the "Use this
 *  product" Spark prefill to namespace the resulting GI's name. */
export function getOfficialSlug(id: string): string | null {
  return OFFICIAL_PRODUCTS.find((p) => p.root_id === id)?.slug ?? null;
}

/** Long-form Overview content for an official product, in the given
 *  locale. Returns `null` for non-officials and for officials that
 *  haven't had their `overview` block authored yet. */
export function getOfficialOverview(
  id: string,
  locale: Locale,
): ProductOverview | null {
  const product = OFFICIAL_PRODUCTS.find((p) => p.root_id === id);
  if (!product) return null;
  return product.copy[locale]?.overview ?? product.copy.en.overview ?? null;
}
